// English translations
import { solutionComponentsEn } from './solutionComponentStrings.js';
import { lensComponentsEn } from './lensComponentStrings.js';
import { moduleIntroEn } from './moduleIntroStrings.js';
import { reportStringsEn } from './reportStrings.js';
import { foresightEn } from './foresightStrings.js';
import { foresightScenarioEn } from './foresightScenarioStrings.js';
import { foresightPathEn } from './foresightPathStrings.js';
import { foresightWildcardEn } from './foresightWildcardStrings.js';
import { futureReimaginedEn } from './futureReimaginedStrings.js';
import { negotiationLabEn } from './negotiationLabStrings.js';
import { kuusiKenttaaEn } from './kuusiKenttaaStrings.js';
import { bookletEn } from './bookletStrings.js';
import { pivotsV3En } from './pivotsV3Strings.js';
import { vipupisteFocusEn } from './vipupisteFocusStrings.js';
import { opinionDatasetsEn } from './opinionDatasetStrings.js';
import { knowledgeKindsEn, sourceTiersEn } from './knowledgeKindStrings.js';
import { instancePromptsEn } from './instancePromptsStrings.js';
import { solutionsV3En } from './solutionsV3Strings.js';
import { kipupisteetV2En } from './kipupisteetV2Strings.js';
import { ankkuritV2En } from './ankkuritV2Strings.js';
import { juurisyytV2En } from './juurisyytV2Strings.js';
import { ongelmakarttaV3En } from './ongelmakarttaV3Strings.js';
import { solutionsV4En } from './solutionsV4Strings.js';
import { futureReimaginedV2En } from './futureReimaginedV2EnStrings.js';

export const en = {
  // Landing Page
  landing: {
    title: 'Welcome to Kuutio.',
    subtitle: 'Choose your role to continue',
    selectInstance: 'Select Kuutio Instance',
    loadingInstances: 'Loading instances...',
    noInstances: 'No instances available',
    sitraUser: 'I am from Sitra',
    invited: 'I was invited',
    whatIsKuutio: '🤔 What is Kuutio?',
    footer: 'This view appears on all computers when starting Kuutio',
    infoModal: {
      title: 'What is Kuutio?',
      paragraph1: 'Kuutio is a co-creation platform developed by Sitra that brings together citizens, decision-makers, and experts to solve key societal challenges.',
      paragraph2: 'The platform enables interactive collaboration across multiple screens and devices. Participants can build impact pathways, share ideas, and visualize complex cause-and-effect relationships together in real-time.',
      paragraph3: 'Kuutio aims to support democratic decision-making and societal change by providing a tool that makes complex phenomena understandable and promotes systems thinking.',
      contactButton: 'Contact us'
    }
  },

  // Individual Impact Path
  individualImpactPath: {
    // Start Modal
    startModal: {
      title: '🌍 Current State: Food System Challenges',
      intro: "Finland's food system is in transition.",
      introDetails: 'Climate change, geopolitical tensions and an aging population challenge traditional practices.',
      question: 'How do we ensure food security when:',
      challenges: [
        'Import dependency is 80% in many product groups',
        'Average age of farmers exceeds 53 years',
        'Extreme weather events are increasing',
        'Food prices rise faster than purchasing power'
      ],
      task: 'Your task:',
      taskDescription: 'Build a path towards a sustainable and more self-sufficient food system. Start by selecting one key question to explore.',
      continueButton: 'Continue'
    },
    
    // Question Selection Modal
    questionModal: {
      title: 'Choose a Focus Question',
      subtitle: 'Pick one of these key questions to explore in your impact pathway:',
      cancelButton: 'Cancel',
      selectButton: 'Select Question'
    },
    
    // Node Modal
    nodeModal: {
      addConsequence: 'Add Consequence',
      addRequirement: 'Add Requirement',
      addComment: 'Add Comment',
      generateWithAI: 'Generate with AI',
      removeNode: 'Remove Node',
      close: 'Close'
    },
    
    // Placeholder nodes
    placeholders: {
      addQuestion: '+ Add Question',
      addNode: '+ Add Node'
    },
    
    // View controls
    viewControls: {
      clustered: 'Clustered',
      individual: 'Individual',
      showAll: 'Show All',
      hideCompleted: 'Hide Completed'
    },
    
    // Settings panel
    settings: {
      title: 'Settings',
      viewMode: 'View Mode',
      clustering: 'Clustering',
      aiComments: 'AI Comments',
      enabled: 'Enabled',
      disabled: 'Disabled'
    }
  },
  
  activityLog: {
    exportFailed: 'Download failed: {{error}}',
  },
  knowledgeManager: {
    exportFailed: 'RAG export failed: {{error}}',
  },

  // Common UI elements
  common: {
    save: 'Save',
    cancel: 'Cancel',
    confirm: 'Confirm',
    ok: 'OK',
    delete: 'Delete',
    confirmDelete: 'Yes, delete',
    deleting: 'Deleting...',
    edit: 'Edit',
    add: 'Add',
    close: 'Close',
    undo: 'Undo',
    redo: 'Redo',
    continue: 'Continue',
    back: 'Back',
    next: 'Next',
    yes: 'Yes',
    no: 'No',
    loading: 'Loading...',
    loadingContent: 'Loading content...',
    error: 'Error',
    success: 'Success',
    unknownSource: 'Unknown source',
    showMoreSources: '+{{count}} more sources',
    showFewerSources: 'Show fewer',
    sourceTitleFallback: 'Source',
    openSource: 'Open source',
    noSources: 'No sources',
    errorBoundaryTitle: 'Something went wrong',
    errorBoundaryUnknown: 'Unknown error',
    errorBoundaryReload: 'Reload',
    unsavedTitle: 'Discard unsaved changes?',
    unsavedMessage: 'Your changes have not been saved. They will be lost if you continue.',
    unsavedLeave: 'You have unsaved changes. Leave anyway?',
    discardChanges: 'Discard changes',
  },

  login: {
    title: 'Sign in',
    subtitle: 'Kuutio workshop admin panel. Sitra staff sign in with their Microsoft account.',
    microsoft: 'Sign in with Microsoft',
    orEmail: 'Outside Sitra and given access? Sign in with a code sent to your email',
    emailNote: 'Use the address you were given access with.',
    hint: 'If you need access, contact the Kuutio team. By signing in you accept that usage may be audited to protect workshop data integrity.',
  },

  pinLogin: {
    title: 'Kuutio',
    chooseSubtitle: 'How are you signing in?',
    chooseEmail: 'I have an invitation to Kuutio with my email address',
    choosePin: 'I am attending a Kuutio workshop and I know the PIN code',
    chooseStaff: 'Sitra Kuutio staff',
    town: {
      title: 'Which town are you from?',
      subtitle: 'The choice is kept on this device: your team\'s answers and conditions are recorded under it. You can change team on the team page.',
    },
    staffSignIn: 'Sign in with Microsoft',
    back: 'Back',
    denied: {
      signin: 'Sign in to open this link.',
      instance: "Your sign-in doesn't give access to this workshop.",
      expired: 'Your session has ended. Sign in again.',
    },
    placeholder: 'PIN',
    submit: 'Sign in',
    invalidPin: 'Invalid PIN code',
    emailPlaceholder: 'name@organisation.fi',
    sendCode: 'Send code',
    sending: 'Sending…',
    codeSentTo: 'Code sent to',
    changeEmail: 'change',
    signingIn: 'Signing in…',
    resend: 'Send a new code',
    resendIn: 'Send a new code ({{s}} s)',
    codeResent: 'New code sent',
    invitedOnly: 'Codes are sent only to invited participants. Use the address you registered with.',
    codeIfInvited: 'If the address is on the invitation list, the code is on its way. Check spam too.',
    connectionError: 'Connection error. Please try again.',
  },

  aiScorePanel: {
    researchSupport: 'Knowledge base',
    relevance: 'Relevance',
    attachments: 'Attachments',
    attachmentsCount: 'Attachments ({{count}})',
  },

  // Solution evaluation tags (IDs match @kuutio/shared/evaluationTags.js)
  evaluationTags: {
    ambitious: 'Ambitious',
    feasible: 'Feasible',
    innovative: 'Innovative',
    creative: 'Creative',
    research_backed: 'Knowledge-base backed',
    systemic: 'Systemic',
    tested_elsewhere: 'Tested elsewhere',
    quick_to_implement: 'Quick to implement',
    uncharted: 'Uncharted territory',
    expert_insight: 'Expert insight',
    bold_hypothesis: 'Bold hypothesis',
    empty: 'No strength tags found.',
    contradicts_research: 'Contradicts knowledge base',
    sectionTitle: 'Tags',
    researchConflictTitle: 'Knowledge base note',
    researchStance: {
      supported: 'Knowledge base supports this',
      silent: 'Knowledge base does not address this',
      silentHint: 'Not a weakness — common for expert and frontier ideas.',
      contradicted: 'Knowledge base contradicts this',
    },
    moreTags: '+{{count}}',
    expandReason: 'Show rationale',
    collapseReason: 'Hide rationale',
  },

  impactPath: {
    resetZoom: 'Show the whole map',
    loadingWorkshop: 'Loading workshop...',
    loadingImpactPath: 'Loading impact path...',
    scenariosLoading: 'Loading scenarios...',
    pathwayLoading: 'Loading pathway...',
    minCharsHint: 'at least 3 characters',
    selectWorkshop: 'Select workshop',
    selectScenario: 'Select scenario',
    scenarioCount: '{{count}} scenarios',
    onlineBadge: 'Online',
    liveBadge: 'Live',
    initializingPaths: 'No paths yet — initializing...',
    chooseFutureFirst: 'Choose a future first, and the impact path starts from its variables.',
    noStarterVariables: 'The scenario has no variables for the impact path to start from.',
    viewMode: {
      mineLabel: 'Mine',
      scenarioLabel: 'Scenario',
      allLabel: 'All',
      mineTitle: 'Mine',
      scenarioTitle: 'Scenario',
      allTitle: 'All',
      mineDescription: 'Only your ideas in this scenario.',
      scenarioDescription: 'Everyone’s ideas in this scenario.',
      allDescription: 'Everyone’s ideas across all scenarios.',
      ownNodeLegend: 'Blue border = your idea',
      switchToMine: 'Switch to Mine',
      switchToAll: 'Switch to All',
      introTitle: 'How to use map views',
      introStepMineTitle: 'Mine',
      introStepMineBody: 'Use Mine when adding and editing your own ideas.',
      introStepAllTitle: 'All',
      introStepAllBody: 'Use All to explore the full workshop map.',
      introStepKeepTitle: 'Your ideas are safe',
      introStepKeepBody: 'Switching views never removes your notes.',
      introConfirm: 'Got it',
      bannerDismiss: 'Dismiss'
    },
    // Valence (§4b): is this consequence one the room wants?
    valence: {
      label: 'Desirability',
      byAi: "AI's judgement",
    },
    votes: { title: 'Does this matter?' },
    notes: { title: 'Notes', placeholder: 'A note on this — a reason, a doubt, a source…', add: 'Add' },
    // Route layout (settings.impactMapLayout = 'route'): goal and milestone cards.
    route: {
      goal: 'Goal',
      start: 'Start',
      milestone: 'Milestone',
      status: { saavutettu: 'Reached', osittain: 'Partly', ei: 'Not reached', avoin: 'Open' },
      progress: 'Progress',
      progressHint: 'Only towns, milestones and goals. Off: the whole map.',
    },
    // The map drawn from the scene (?scene=1): card kinds and connector words.
    scene: {
      loading: 'Loading the map…',
      error: 'The map could not be loaded: {{message}}',
      empty: 'There is nothing on the map yet.',
      kind: {
        mission: 'Start', start: 'Start', done: 'Done', goal: 'Goal', milestone: 'Milestone',
        blocker: 'Blocker', avoider: 'Way to avoid', input: 'Input', output: 'Output', outcome: 'Outcome',
        impact: 'Impact', consequence: 'Consequence', condition: 'Condition', question: 'Question', target: 'Change goal', endState: 'End state',
      },
      doneGoals: '{{count}} goals',
      axis: {
        quarter: 'Q{{n}}', week: 'wk {{n}}', round: 'round {{n}}',
        report: 'Report {{date}}: {{count}} cards',
        noDates: 'The cards in view have no dates',
        basis: { stated: '{{date}}', due: 'due {{date}}', reported: 'by {{date}}' },
      },
      edge: {
        leads_to: 'leads to', enables: 'enables', blocks: 'blocks', reinforces: 'reinforces', weakens: 'weakens',
        delays: 'delays', relates_to: 'relates to', is_same: 'same thing', asks: 'asks',
      },
    },
    // Adding a blocker on an edge: stopping a wanted consequence, or avoiding an unwanted one.
    // The node modal's neighbourhood (impactmap/neighbourhood): what leads to a card and what follows
    proposedLink: {
      title: 'Proposed link', by: 'AI\'s reason', accept: 'Accept', reject: 'Reject', failed: 'The decision was not saved (HTTP {{status}}).',
    },
    neighbourhood: {
      into: 'Leads here', outOf: 'Follows from this', none: 'Nothing yet', start: 'Start', avoids: 'avoids',
      error: 'The map\'s connections could not be loaded.',
      source: { documents: 'Drafted from the project\'s own reports', ai: 'Suggested by the AI', person: 'Written by a participant' },
    },
    blockerDraft: {
      block: { label: 'A blocker stops this', title: 'Add a blocker', subtitle: 'What stops this from happening?' },
      avoid: { label: 'An unwanted consequence to avoid', title: 'Add a way to avoid it', subtitle: 'How is this avoided?' },
    },
    // A card drafted from the team's own documents, awaiting a person.
    proposed: {
      title: 'Drafted from your documents',
      tag: 'from documents',
      lead: 'This is a draft from your own reports. Keep it, soften it in your own words, or reject it.',
      provenanceTitle: 'Based on the sentence',
      keep: 'Keep',
      soften: 'Soften',
      softenSave: 'Save softened',
      softenLabel: 'Write it in your own words',
      reject: 'Reject',
      reasonPlaceholder: 'Reason (optional)',
      decided: { keep: 'Kept', soften: 'Softened in own words', reject: 'Rejected' },
    },
    nodeModal: {
      headerTitle: 'What follows from this?',
      parentLabel: 'Previous',
      titleLabel: 'Title',
      consequencePlaceholder: 'Write the consequence here...',
      thoughtTitlePlaceholder: 'Idea title...',
      thoughtDescPlaceholder: 'Idea description...',
      aiStreaming: 'Writing...',
      aiLoading: 'Loading...',
      completeLabel: 'Complete',
      closeTitle: 'Close',
      cancel: 'Cancel',
      commentsTitle: 'Comments',
      addImpact: 'Add impact',
      deleteWarning: 'This will also remove all {{count}} sub-ideas.',
      deleteBlocked: 'The node has {{count}} consequence nodes added by other participants. Deletion is not allowed.',
      saveFailed: 'Save failed. Please try again.',
      deleteFailed: 'Could not delete the idea. Please try again.',
      describeConsequenceLabel: 'DESCRIBE THE CONSEQUENCE',
      titleFieldLabel: 'TITLE',
      descriptionFieldLabel: 'DESCRIPTION',
      aiAnalyzingThought: 'AI is analyzing the idea...',
      editButtonTitle: 'Edit',
      editAdjust: 'Adjust',
      creating: 'Creating...',
      analyzing: 'Analyzing...',
      continue: 'Continue',
      saving: 'Saving...',
      saveOrAnalyze: 'Analyze & save',
      defaultNodeTitle: 'Idea',
      researchBacked: 'Research-backed',
      regenerate: 'Regenerate',
      replaceAiTitle: 'Replace current AI text',
      completeAiTitle: 'Complete text with AI',
      deleteBlockedAlert: 'This idea has content added by other participants. Deletion is not allowed.',
      commentFailed: 'Could not add comment. Please try again.',
    },
    aiAnalysis: {
      loadingTitle: '⏳ AI is analyzing...',
      loadingDescription: 'Creating three perspectives: blocker, opportunity, question',
      analysisFailedTitle: '❌ AI analysis failed',
      serviceUnavailable: 'The AI service is not available right now',
      serviceOverloaded: 'The AI service is overloaded. Try again in a moment.',
      serviceInternalError: 'Internal AI service error. Please try again.',
      generationErrorTitle: '❌ Error in AI analysis',
      generationErrorDescription: 'Technical error. Try again or continue without AI assistance.',
      academicQuestion: 'Analyze the impact-path step "{{nodeText}}" and give three perspectives: obstacle, opportunity, question. Ground the answer in research.',
    },
    consequenceFlow: {
      connectionAdded: 'Idea "{{node}}" added as a consequence of "{{parent}}"',
      connectionExists: 'This connection already exists.',
      connectionFailed: 'Creating the connection failed. Try again.',
      networkError: 'Network error. Check the connection and try again.',
      minCharsAlert: 'Enter at least 3 characters',
    },
    existingNodeSelector: {
      minCharsAlert: 'Enter at least 3 characters',
      title: 'Add consequence',
      parentNodeLabel: 'Previous node',
      loadingSuggestions: 'Fetching suggestions...',
      instruction: 'Write a title for the consequence below. Click "Continue" to find similar nodes and AI suggestions.',
      titlePlaceholder: 'Consequence title...',
      relatedSources: 'Related sources:',
      sourceFallback: 'Source',
    },
    nodeType: {
      scenario: 'Scenario',
      variable: 'Variable',
      consequence: 'Consequence',
      start: 'START',
    },
    changeScenario: 'Change scenario',
    infoTab: 'Info',
    infoPanel: {
      workshop: 'Workshop',
      participants: '{{count}} participants',
      perspectiveLabel: 'Perspective focus',
      scenarios: 'Scenarios ({{count}})',
      active: 'Active',
    },
    settings: {
      userInfoSection: 'User info',
      userIdLabel: 'User ID',
      userIdDesc: 'Your current participant identifier',
      notSet: 'Not set',
      consequenceSection: 'Consequence creation',
      twoStepLabel: 'Two-step consequence flow',
      twoStepDesc: 'Guide users through creating consequences in two steps: first text, then metadata.',
      aiSection: 'AI',
      aiAnalysisLabel: 'AI analysis for nodes',
      aiAnalysisDesc: 'Research support and relevance scores for nodes. Disable if the service is slow.',
      beta: 'beta',
    },
    duplicateCheck: {
      title: 'Choose impact',
      older: 'Older',
      newer: 'Newer',
      combined: 'Combined',
      existingFallback: 'Existing impact',
      newFallback: 'New impact',
      combinedFallback: 'Combined impact',
      cancel: 'Cancel',
      accept: 'Accept selected',
    },
    addNode: {
      kindLabel: 'What is this in the chain?',
      title: 'What follows from this?',
      titleInstead: 'What could happen instead?',
      subtitle: 'In your own words. The node goes on the map at once; the AI condenses a title and looks for similar nodes after that.',
      save: 'Add to the map',
      added: 'Added: {{title}}',
    },
    createNode: {
      titleLabelOptional: 'Title (optional)',
      titleHintAi: 'Leave empty and the AI condenses one from the description',
      titlePlaceholderAi: 'The AI condenses a title from the description',
      descriptionHintLong: 'As long as you like',
      optional: 'Optional',
      titleRequired: 'Title is required',
      title: 'Create new node',
      subtitle: 'Fill in the node details',
      parentLabel: 'Previous node',
      titleLabel: 'Title *',
      titlePlaceholder: 'E.g. Climate change',
      descriptionLabel: 'Description',
      descriptionPlaceholder: 'Describe in more detail what this means...',
      researchSources: 'Research sources ({{count}})',
      back: 'Back',
      save: 'Save',
    },
    aiConsequence: {
      title: 'AI suggests consequences',
      subtitle: 'Choose what to add',
      targetNode: 'Target node:',
      loading: 'AI is analyzing and generating suggestions...',
      description: 'AI suggests these consequences based on research and context. You can edit suggestions before adding them.',
      titlePlaceholder: 'Title...',
      descriptionPlaceholder: 'Description...',
      aiGenerated: 'AI-generated',
      confidence: 'Confidence: {{percent}}%',
      edited: '✏️ Edited',
      selectedOne: 'consequence',
      selectedMany: 'consequences',
      selectedPrefix: 'Selected:',
      addSelected: 'Add selected',
      empty: 'AI could not generate suggestions.\nTry again or add a consequence manually.',
    },
    painPointVote: {
      title: 'Pain points: {{scenario}}',
      finish: 'Finish voting and form the muuttujat',
      finishing: 'Counting votes…',
      finished: 'Voting closed: {{votes}} votes, {{count}} muuttujat on the map.',
      facilitatorOnly: 'Only the facilitator can finish the vote.',
    },
    didYouMean: {
      title: 'Suggestions for topic',
      subtitle: 'AI is analyzing research sources',
      searching: 'Searching:',
      parentLabel: 'Previous node',
      loading: 'Fetching research sources and creating suggestions...',
      researchSources: 'Research sources used ({{count}})',
      aiSuggestions: 'AI suggestions',
      moodPositive: '👍 Positive',
      moodNegative: '👎 Negative',
      moodQuestion: '❓ Question',
      moodNeutral: '➖ Neutral',
      existingSimilar: 'Existing similar nodes',
      similarity: '{{percent}}% similar',
      empty: 'No similar nodes or suggestions found.\nCreate a new node below.',
      createNew: 'Create your own node',
    },
    similarAfterAdd: {
      title: 'Similar nodes on the map',
      intro: 'Is what you added the same as one of these? Pick it and they are marked the same. Your node stays on the map either way.',
      notSame: 'Not the same',
      found: '{{count}} similar nodes on the map: "{{title}}"',
      show: 'Show',
      dismiss: 'Dismiss',
      similarity: '{{percent}}% similar',
    },
    consequenceSelect: {
      title: 'Select consequences',
      subtitle: 'Attach to current node: {{text}}',
      existingSection: 'Existing nodes',
      existingDesc: 'We found these similar nodes in the tree. You can link to them and add a comment.',
      existingBadge: 'Existing',
      depth: 'Depth: {{depth}}',
      aiSection: 'AI suggestions',
      aiDesc: 'AI suggests these new consequences based on research and context.',
      aiGenerated: 'AI-generated',
      confidence: 'Confidence: {{percent}}%',
      empty: 'No suggestions available. Try again or add a consequence manually.',
      loading: 'Loading suggestions...',
      selectedOne: 'consequence',
      selectedMany: 'consequences',
      selectedPrefix: 'Selected:',
      addSelected: 'Add selected',
    },
    academicReview: {
      fillAll: 'Fill in title and description for all three perspectives.',
      title: 'Review AI perspectives',
      subtitle: 'Edit the texts if needed before saving. Maximum title length is {{max}} characters.',
      labelBlocker: 'Blocker (blocker)',
      labelPossibility: 'Possibility (possibility)',
      labelQuestion: 'Question (question)',
      titleLabel: 'Title',
      descriptionLabel: 'Description',
      cancel: 'Cancel',
      saveNodes: 'Save nodes',
    },
  },

  futureReimagined: futureReimaginedEn,
  negotiationLab: negotiationLabEn,
  kuusiKenttaa: kuusiKenttaaEn,
  booklet: bookletEn,
  pivotsV3: pivotsV3En,
  vipupisteFocus: vipupisteFocusEn,
  opinionDatasets: opinionDatasetsEn,
  knowledgeKinds: knowledgeKindsEn,
  sourceTiers: sourceTiersEn,
  instancePrompts: instancePromptsEn,
  solutionsV3: solutionsV3En,
  kipupisteetV2: kipupisteetV2En,
  ankkuritV2: ankkuritV2En,
  juurisyytV2: juurisyytV2En,
  ongelmakarttaV3: ongelmakarttaV3En,
  ratkaisutV4: solutionsV4En,
  futureV2: futureReimaginedV2En,
  ratkaisutV2: {
    views: { analysis: "Analysis" },
    analysisTitle: "Solutions and their assessment",
    analysisIntro: "All the workshop's solutions, how they work together, and the AI's estimate of what each would do to the map's leverage points.",
    noProblem: "No problem",
    work: {
      title: "Which leverage point do you take on?",
      hint: "Choose a leverage point: a direction something must change in. The map shows the root causes it carries and the problems they belong to, and then the conversation helps you write a solution that moves it.",
      pivotMeta: "Problems: {{problems}} · Solutions: {{solutions}}",
      focused: "The choice of leverage points is locked: the chosen ones are offered.",
      empty: "The map has no leverage points yet.",
      focusHint: "The leverage point's root causes, obstacles to change and problems on the map. Tap to go on.",
      pivotLabel: "Leverage point",
      allPivots: "All leverage points…",
      leaveDraft: "The solution draft will be left unfinished. Switch anyway?",
    },
    effects: {
      title: "Possible effects",
      hint: "What the draft could do to the leverage points in this part of the map. Once saved, the solution is assessed against every leverage point.",
      empty: "The conversation brings up the effects once the solution starts to take shape.",
      intended: "Intended effects",
      side_effect: "Possible side effects",
      worsens: "the wrong way",
    },
    setAnalysis: {
      title: 'The solutions as a whole',
      intro: 'The AI reads the solutions together: where they pull against each other, against interests or against research, what they leave unaddressed, and whether something similar has been tried elsewhere. Every finding has its own sources.',
      needsSolutions: 'Write some solutions first, then they can be analysed as a whole.',
      waiting: 'The analysis of the set has not been run yet. The facilitator starts it, and the result appears here when it is ready.',
      stale: 'The solutions have changed since this analysis. The reading is of an earlier state.',
      unaddressed: 'Leverage points no solution takes on',
      allAddressed: 'Every leverage point is taken on by at least one solution.',
      recommended: 'Recommended by the map analysis',
      triedElsewhere: 'Tried elsewhere',
      newsOnly: 'Rests on a news source only — not research.',
      outcome: { worked: 'Worked', mixed: 'Mixed', failed: 'Did not work', unclear: 'Unclear' },
    },
    portfolioTitle: "What the solutions move together",
    portfolioHint: "{{count}} solutions against the map’s leverage points. A red arrow means that solution moves the metric the wrong way.",
    contested: "Conflict",
    untouched: "No solution touches these yet ({{count}})",
    chartMeasured: "Relative change — the metric has a statistics series",
    chartProposed: "Shape only — proposed metric, no measurement data",
    today: "Today",
    yearsOn: "years on",
    range: "Range",
    midpoint: "Midpoint",
    chartLegend: "This is a scenario, not a forecast: the dashed line is an estimate and the shading is its uncertainty. The further out, the wider it gets.",
    tagDerived: "Computed from the projection",
    tags: {
      marginal: "Marginal effect",
      displaces_problem: "Displaces the problem",
      slow_burn: "Slow burn",
      wide_reach: "Wide reach",
      breaks_a_loop: "Hits a loop",
      no_way_in: "No way in",
      already_tried: "Already tried",
      depends_on_unlikely: "Rests on the unlikely",
      research_backed: "Research-backed",
      tested_elsewhere: "Works elsewhere",
      systemic: "Structural",
      creative: "Creative",
    },
    scoresTitle: "Assessment",
    ambitionUnreachable: "Ambitious but with no way in: no step on the pathway could begin with today’s actors and mandate. That does not make the solution bad, but the first step is missing.",
    pathwayMissing: "No pathway described. Without steps and actors there is nowhere to begin.",
    pathwayTitle: "Pathway",
    reasoningTitle: "Reasoning",
    stepCount: "{{count}} steps",
    blockersTitle: "Blockers",
    now: "Now",
    precondition: "Requires",
    couldRemove: "Could remove it",
    score: { feasibility: "Feasibility", ambition: "Ambition", effectiveness: "Effectiveness" },
    scoreHint: { feasibility: "Could work begin this year with today’s actors?", ambition: "How fundamental a change is this?", effectiveness: "How much does this move the map?" },
    improve: {
      title: "Improve the solution",
      intro: "Ask, challenge the assessment or suggest a change. When the conversation produces a better version, it is proposed to you. An accepted improvement is assessed again, and the old version is kept.",
      placeholder: "Answer, ask or suggest a change…",
      readAssessment: "Read the assessment",
      writeNew: "Write a new solution",
      versionAccepted: "Version {{version}} accepted",
      stale: "The figures are from the previous version.",
    },
    previousScore: "v{{version}}: {{value}}",
    tagNew: "new",
    tagsRemoved: "Dropped:",
    versions: { title: "Versions", current: "current" },
    wall: {
      title: "Solutions",
      lead: "The assessment updates after every accepted improvement.",
      justImproved: "Just improved",
      next: "Next: Future testing. Together we choose which solutions go into the simulation.",
      empty: "No solutions written yet.",
    },
    movesLabel: "What the solution moves",
    noPivot: "No leverage point",
    problemsReached: {
      title: "Problems the solution takes on",
      solves: "solves",
      touches: "eases",
      none: "The assessment finds the solution takes on none of the workshop's problems.",
    },
    proposedEdit: "Proposed edit to the solution",
    acceptEdit: "Accept the edit and re-evaluate",
    accepting: "Re-evaluating…",
    back: "Back to solutions",
    edit: "Edit",
    showAll: "Show the whole text",
    showLess: "Show less",
    running: "Evaluating…",
    runningHint: "The AI goes through every leverage point. The assessment finishes in a few minutes, even if you leave the page.",
    notEvaluated: "The effects have not been evaluated yet.",
    moves: "Moves {{count}} metrics",
    key: "↑ the metric rises · ↓ the metric falls · red card: a change for the worse",
    figuresExplained: "What do the figures mean?",
    figuresHelp: "The figure is the AI's estimate of how many per cent the metric would change in five years if the solution were carried out; the range is in brackets. The basis says whether the estimate rests on research, a comparison from elsewhere, or reasoning. Open a card for the mechanism, the other years and the risks.",
    whyAndWhen: "Why and when",
    risks: "{{count}} risks",
    unchanged: "No effect ({{count}})",
    worsens: "Makes worse",
    intended: "Intended",
    sideEffect: "Side effect",
    year: "{{n}} y",
    revisedFrom: "Version {{from}} → {{to}}",
    noEffect: "no effect",
    wasBefore: "previously {{value}}",
    revisedHint: "The figures come from the new estimate of the revised solution. The dialogue does not set them.",
    lag: "{{months}} mo lag",
    watchFor: "Early sign",
    threatDelays: "Delays",
    threatReduces: "Reduces",
    threatBlocks: "Blocks",
    basis: { research: "Research", analogue: "Analogue", reasoned: "Reasoned" },
    basisSplit: "Basis: research {{research}} · analogue {{analogue}} · reasoned {{reasoned}}",
    adverseFound: "Most likely downside",
    adverseNone: "No adverse effect found",
    title: "Solutions",
    intro: "Write a proposal. The analysis evaluates it against every leverage point on the map — including the ones you were not thinking about.",
    write: "Write a solution",
    contextTitle: "The map’s leverage points",
    contextHint: "These are the metrics your workshop found. A solution is measured against all of them.",
    noVipupisteet: "No leverage points derived yet. Run the leverage analysis on the problem map first.",
    listTitle: "Solutions ({{count}})",
    empty: "No solutions yet.",
    compose: {
      intro: "Tell us your solution in your own words. Kuutio asks one thing at a time — what the analysis needs to judge it fairly — and writes up a draft. The proposal stays yours.",
      startPlaceholder: "What would you like to do?",
      start: "Start",
      replyPlaceholder: "Reply here…",
      send: "Send",
      thinking: "Thinking…",
      summaryTitle: "Proposal and assessment",
      yourProposal: "Your proposal",
      yourProposalEmpty: "What you propose gathers here — in your words.",
      draft: "Draft",
      draftHint: "You can edit the draft directly. Your edits go into the next turn.",
      missing: "The analysis still lacks",
      missingNone: "The solution has everything the analysis needs.",
      suggestions: "Suggestions from Kuutio — tap one to add it to your reply",
      takeSuggestion: "Let's include: {{s}}",
    },
    modalTitle: "Solution proposal",
    modalIntro: "Write it in your own words. Next the AI estimates which metrics this would move, and by how much.",
    titleLabel: "Title",
    titlePlaceholder: "A short name for the solution",
    bodyLabel: "The solution",
    bodyPlaceholder: "What is done, who does it, and what changes?",
    save: "Save solution",
  },
  systemicMapV2: {
    whatIsThis: "What is this?",
    bindTitle: "Statistical binding",
    bindIntro: "This is a proposed metric. Find it a real Statistics Finland series so projections start from a measured baseline.",
    bindSearch: "Find a series",
    bindSearching: "Searching…",
    bindSearchingLong: "Searching Statistics Finland… {{elapsed}} s. The search goes through the statistics catalogue and can take a minute.",
    bindFailed: "Something went wrong. Try again in a moment.",
    bindConfirm: "Confirm this series",
    binding: "Binding…",
    bindPoints: "{{count}} observations",
    bindNone: {
      no_topic: "Nothing in the Statistics Finland catalogue measures this. The metric stays proposed; it can still be used in the discussion.",
      no_tables: "The topic has no statistics tables. The metric stays proposed; it can still be used in the discussion.",
      no_table_match: "Statistics Finland has no table that measures this. The metric stays proposed; it can still be used in the discussion.",
      unpinned_dimensions: "Statistics were found, but none matched this metric exactly, so nothing was bound on a guess. The metric stays proposed.",
      no_series: "The series could not be fetched just now. You can try again in a moment.",
    },
    statfiReview: {
      waiting: "{{count}} waiting",
      title: "Statistics for the metrics",
      intro: "The search looks for a Statistics Finland time series for every proposed metric. Nothing is bound without confirmation: check the table name, what the series measures and the latest values, then confirm or skip.",
      preparing: "Preparing the search…",
      progress: "{{stage}} · {{searched}} / {{metrics}} metrics done",
      stages: {
        catalogue: "Checking the catalogue",
        triage: "Choosing topics",
        tables: "Choosing tables",
        metadata: "Fetching table details",
        filters: "Narrowing the queries",
        series: "Fetching series",
        stewards: "Proposing who measures",
      },
      summary: "{{measured}} bound · {{review}} awaiting review · {{none}} with no suitable series · {{skipped}} skipped · {{errors}} errors · {{pending}} not searched",
      skip: "Skip metric",
    },
    statfiSource: {
      label: "Source:",
      provider: "Statistics Finland",
      table: "table {{table}}",
      open: "Open table",
    },
    steward: {
      noDataset: "No Statistics Finland dataset was found for this metric.",
      title: "Who could measure this?",
      practice: "In practice",
      responsible: "Responsible for the statistics",
      basis: "A proposal reasoned by AI, without a source.",
      pending: "A proposal of who could measure this is written on the next statistics search.",
    },
    loopsTitle: "Loops",
    loopsIntro: "A loop forms when a leverage point feeds back into a root cause. These are the map’s only inferred edges — everything else exists because someone wrote one node under another. The bar is high and an empty result is a valid answer.",
    noLoops: "No loops. That is not a fault: the material does not yet contain two leverage points whose root causes act on each other with enough confidence.",
    reinforcing: "Reinforcing circle",
    balancing: "Balancing",
    conditional: "Conditional",
    conditionalHint: "The loop contains a precondition. It runs only while the precondition holds.",
    measuresLink: "metric",
    effectTypes: {
      leads_to: "leads to",
      reinforces: "reinforces",
      weakens: "weakens",
      nullifies: "cancels out",
      enables: "enables",
      blocks: "blocks",
      requires: "requires",
      delays: "delays",
      relates_to: "relates to",
      is_same: "same thing",
    },
    loopSteps: "{{count}} steps",
    noNodeReasoning: 'No reasoning of its own has been recorded for this node.',
    carousel: {
      clear: 'Clear selection',
      causes: "Causes",
      effects: "Effects",
      lateral: "Related",
      back: "Back",
    },
    legend: {
      title: "Connection colours",
      toggle: "Show or hide the legend",
      families: {
        amplify: "Amplifies",
        dampen: "Dampens",
        enabling: "Enables",
        neutral: "Related",
      },
      untyped: "No type yet",
    },
    lists: {
      title: 'Map lists',
      all: 'All',
      nodes: 'Nodes',
      edges: 'Connections',
      loops: 'Loops',
      search: 'Search…',
      none: 'Nothing matches.',
      noLoops: 'The map has no loops yet.',
      nodeTypes: { kipupiste: 'Problem', este: 'Obstacle to change', juurisyy: 'Root cause', vipupiste: 'Leverage point' },
      edgeTypes: { petal: 'Obstacle → problem', mechanism: 'Root cause → obstacle', measures: 'Leverage point', causal: 'Feedback' },
      strength: { strong: 'strong', weak: 'weak' },
      open: 'Open',
      loopKinds: { conditional: 'Conditional', reinforcing: 'Reinforcing', balancing: 'Balancing' },
    },
    edge: {
      details: 'Details',
      nature: 'Nature of the connection',
      reasoning: 'Reasoning',
      loopPath: 'Loop route',
      metric: 'Metric',
      noReasoning: 'No reasoning has been recorded for this connection.',
      kinds: { petal: 'The obstacle to change holds the problem in place: while the obstacle holds, the problem does not ease.', mechanism: 'The root cause keeps the obstacle up: it is the reason the obstacle holds.', measures: 'The leverage point says which way the thing behind the root cause must change, and its metric how the root cause can be tracked. The link claims no influence.', causal: 'Feedback: when the leverage point moves, the target changes. This connection was inferred by the AI from the map.' },
      sign: { '+': 'When the cause grows, the effect grows.', '-': 'When the cause grows, the effect weakens.' },
      title: 'Connection',
      from: 'Cause',
      to: 'Effect',
      kind: 'Kind',
      relation: 'Mechanism',
      assumption: 'Assumption',
      testQuestion: 'Test question',
      counterArgument: 'Counter-argument',
      confidence: 'Confidence',
      aiMade: 'Connection inferred by the AI',
    },
    analysisTitle: "Workshop analysis",
    analysisIntro: "A heavy read over the whole material at once: every problem, obstacle to change, root cause and verdict. Separates what the workshop showed, what follows from it, and what is not known.",
    analysisWaiting: "The analysis has not been run yet. The facilitator starts it, and the result appears here when it is ready.",
    staleWarning: "The map has changed since this analysis. The reading is of an earlier state.",
    overview: "The whole picture",
    patternTitle: "The shape of the whole",
    contradictions: "Contradictions",
    gaps: "Gaps and thin spots",
    tensions: "Tensions and disagreements",
    blindSpots: "Blind spots",
    reasoningOnly: "The analysis's own reasoning — the sources do not show a disagreement",
    tensionKind: { room: "In the room", interests: "Interests", research: "Research", room_vs_research: "Room vs research", solutions_conflict: "Between solutions" },
    blindSpotKind: { research_not_on_map: "In the research, not on the map", unaddressed_dimension: "Not addressed", alternative_frame: "Another framing", unaddressed_lever: "Leverage point without a solution", research_not_addressed: "In the research, not in the solutions", alternative_approach: "Another approach" },
    webSearchFailed: "The web search failed, so the analysis rests on the workshop's own material only.",
    recommendation: "Where to go next",
    generatedAt: "Analysis run {{when}}",
    whyTitle: "Why this rose",
    established: "The data shows",
    follows: "From this follows",
    notKnown: "This is not known",
    noAnalysisForNode: "The analysis has not been run yet. Run it below the map to get the reasoning.",
    reachTitle: "Reach",
    metricsTitle: "Leverage points",
    verdictsTitle: "What the room judged",
    verdictsHint: "{{count}} verdicts. That is a count of people, not a consensus.",
    carriesTitle: "Root causes this leverage point acts on",
    lever: { what: 'What the leverage point is', why: 'Why this direction', how: 'How it could be measured', metric: 'Metric: {{label}}', details: 'Why this direction, and how it is measured', pending: 'This leverage point has not been worded yet. The facilitator starts the wording; until then you see the metric that follows it.' },
    introGraph: 'The same workshop as one network: problems, obstacles to change, root causes and the leverage points that measure them. Drag, zoom and turn. Leverage points are green.',
    views: { 'analysis': 'Analysis', '2d': '2D map', 'sphere': '3D sphere', 'galaxy': 'Galaxy' },
    map3dFailed: { title: 'The 3D view could not start', hint: 'Your browser gave the 3D view no WebGL context (graphics acceleration off, or no GPU?). The 2D map and the lists work without it.', back: 'Back to the 2D map' },
    title: 'Problem mapping',
    intro: 'The workshop as one picture. Problems around the ring, their root causes beside them, and in the middle the leverage points — quantities that more than one root cause moves. The more problems a leverage point reaches, the closer to the centre it sits.',
    vipupisteetTitle: 'Leverage points',
    vipupisteetHint: 'Ranked by how many problems the quantity reaches into. One that reaches several is the one whose movement is felt in more than one place.',
    noVipupisteet: 'No shared leverage points on the map yet. A leverage point appears on the map when several root causes share it.',
    reach: '{{juurisyyt}} root causes · {{kipupisteet}} problems',
    carries: 'Carries {{count}} root causes:',
    legendApproved: 'Approved root cause',
    legendRejected: 'Rejected',
    legendUnjudged: 'Not judged',
    legendApproval: 'Root cause colour: approval 0 % (red) – 100 % (green), grey = no votes',
    sources: {
      fold: 'Sources ({{count}})',
      web: 'Web sources',
      webNote: 'Searched online, limited to vetted publishers.',
      openPage: 'Open page',
      answered: 'Answered',
      pageAge: 'Published',
      credibility: { research: 'Research', official: 'Official', organisation: 'Organisation', interest: 'Interest group', news: 'News' },
      unavailable: 'The source service did not answer — the reasoning could not be checked against the material.',
      none: 'No source in the workshop material.',
      workshop: 'Workshop material',
    },
    review: {
      noVotes: 'no votes',
      approval: '{{pct}} % · {{votes}} votes',
      earlierVersion: 'v{{version}}: {{approve}}/{{votes}} approved, refined',
    },
    admin: {
      summary: '{{cards}} root causes · {{voted}} voted · {{kept}} kept · {{revised}} revised · {{toDelete}} to be deleted · {{culled}} removed · {{open}} not discussed',
      openHint: 'Judgements are open. Lock them with the flow-control button once the discussion is done.',
      lockedBy: 'Locked {{at}} ({{by}})',
      unlock: 'Unlock',
      unlockReason: 'Why unlock? The reason is recorded.',
      swipeTab: 'Swiping',
      discussionTab: 'Discussion',
      deckTally: '{{cards}} cards · {{voted}} voted · {{votes}} votes',
      tally: '👍 {{approve}} · 👎 {{reject}} · ✎ {{refine}}',
      splitSelf: 'Authors 👍 {{approve}} · 👎 {{reject}} · ✎ {{refine}}',
      splitOutside: 'Others 👍 {{approve}} · 👎 {{reject}} · ✎ {{refine}}',
      onWall: 'Show on the wall',
      offWall: 'Off the wall',
      keep: 'Keep',
      edit: 'Edit',
      reject: 'Reject',
      undoReject: 'Undo rejection',
      askAi: 'Ask the AI',
      notePlaceholder: 'What is the room saying? (the AI uses this)',
      saveVersion: 'Save new version (approved)',
      culled: 'Removed from the map',
      wallEmpty: 'The root cause under discussion appears here when the facilitator puts it on the wall.',
      outcomes: { keep: 'Kept', revise: 'Revised', reject: 'To be deleted' },
      moderatePrompt: 'The room voted on this root cause: {{approve}} approved, {{reject}} rejected, {{refine}} asked for refinement. The room says: {{note}}. Briefly name what the disagreement seems to be about, and propose a sharper wording.',
    },
    legendMeasured: 'Measured leverage point',
    legendProposed: 'Proposed leverage point',
    legendLateral: 'Reaches several problems',
  },
  juurisyyV2: {
    generation: {
      loading: 'Loading…',
      neverRun: 'The root causes have not been generated yet. The facilitator starts it.',
      running: 'Generating root causes… {{done}} / {{total}} obstacles',
      connecting: 'Connecting root causes and leverage points…',
      building: 'The map is growing…',
      failed: 'Not everything could be generated. You can still open the map.',
      doneTitle: 'The root causes are generated',
      doneHint: 'The map is ready to be judged.',
      open: 'Open the map',
      openAnyway: 'Open the map anyway',
      fallback: 'The 3D picture cannot be shown on this device.',
    },
    title: 'Root causes',
    loading: 'Loading…',
    error: 'Something went wrong',
    card: {
      whyTitle: 'Why this root cause',
      metricsTitle: 'Leverage points and metrics',
      noMetrics: 'This root cause has no leverage point yet.',
      shows: 'What it would tell',
      latest: 'Latest value: {{value}} ({{period}})',
      table: 'Statistics Finland: {{table}}',
      sources: 'Sources ({{count}})',
    },
    map: {
      hint: "Tap a root cause to vote on it. Tap an obstacle to propose a new one.",
      all: "All",
      voteTitle: "Vote on this root cause",
      proposeTitle: "Propose a root cause for this obstacle",
      yourVote: "Your vote",
      noVote: "You have not voted on this yet",
      votes: "Votes",
      earlier: "Earlier versions",
      resetsVotes: "Editing starts a new version: the current {{count}} votes stay with the earlier version and the room votes again.",
      resetsNone: "Editing starts a new version for the room to vote on.",
      stale: "This root cause was edited. This is the current version: vote on it.",
      lockedNote: "Judgements are locked: votes no longer change.",
      proposed: "Proposed",
      loadFailed: "Could not load the root cause",
      counts: { approve: "Approved", reject: "Rejected", refine: "Would refine" },
    },
    cardsLeft: 'Cards left: {{count}}',
    intro: {
      title: 'How this works',
      body: 'The AI derived these root causes from your obstacles to change. Your job is to judge them — not to write new ones.',
      approve: 'Approve: this is a real cause in our world.',
      reject: 'Reject: this is not right. The card is kept; the rejection is recorded.',
      refine: 'Refine: something is off. You say what, and you get a new version to judge.',
      swipeHint: 'You can also swipe the card: right approves, left rejects.',
      start: 'Start',
    },
    add: {
      open: 'Add the missing root cause',
      pickAnchor: 'Which obstacle is this a root cause of?',
      pickAnchorHint: 'Pick the obstacle to change this root cause sustains.',
      noAnchors: 'This problem has no obstacles to change yet.',
      writeTitle: 'Write the root cause in your own words',
      writeHint: 'Say what sustains this obstacle. The AI shapes it into a card — it does not replace your point with its own.',
      placeholder: 'We think what is behind this is…',
      proposalLabel: 'Proposed card',
      duplicate: 'This is already in the deck, in other words:',
      save: 'Save to the map',
      saved: 'Saved',
      changeAnchor: 'Change obstacle',
      cancel: 'Cancel',
    },
    approve: 'Approve',
    reject: 'Reject',
    refine: 'Refine',
    metricMeasured: 'Measured',
    metricMeasurable: 'Proposed metric — a statistic likely exists',
    metricCount: '{{count}} metrics',
    metricProposed: 'Proposed metric — no measurement data',
    refineTitle: 'What is wrong with this?',
    refinePlaceholder: 'Tell us in your own words…',
    send: 'Send',
    thinking: 'Thinking…',
    newVersion: 'New version',
    acceptVersion: 'Judge this version',
    cancelRefine: 'Back to the card',
    deckDone: 'The deck is done.',
    locked: 'The judgements are locked',
    lockedHint: 'The facilitator has locked the root-cause judgements. Voting has closed.',
    why: {
      title: 'Why this card?',
      falsifier: 'Wrong if:',
      ownReasoning: 'Deliberately reasoning:',
      more: 'What would make this false, and the reasoning behind it',
      unverified: 'The research check did not finish for this card.',
      unverifiedRefined: 'Wording refined by the group: not yet checked against the research.',
      basis: { research: 'Research', analogue: 'Analogue', reasoned: 'Reasoning' },
    },
    links: {
      title: 'Connections',
      shared: 'Shared with {{count}} other root causes (problems: {{kipupisteet}})',
      alone: 'Not yet shared with other root causes',
      vipupiste: 'On the map as a leverage point',
      show: 'show',
      hide: 'hide',
      inLoop: 'Part of a feedback loop',
    },
    notFound: 'Did the central root cause not come up?',
    notFoundHint: 'If something essential is missing, tell us.',
    backToDecks: 'Back to your decks',
    decks: {
      title: 'Your decks',
      hint: 'Judge the root causes one card at a time: swipe right to approve, left to reject, or ask for a refinement.',
      own: 'Your obstacles to change',
      ownHint: 'The AI derived these root causes from the obstacles you wrote. Did it get them right?',
      others: "Others' obstacles",
      othersHint: 'An outside look at obstacles others wrote.',
      left: '{{count}} / {{total}} left',
      done: 'Done',
      allDone: 'You have been through all your decks. Thank you!',
      more: 'Rate more',
      noMore: 'Every obstacle to change has already been dealt out for judging.',
      none: 'No decks for you yet: the root causes have not been derived yet, or they have already been decided.',
      nextDeck: 'Next deck',
    },
    version: 'version {{n}}',
  },
  kipupisteV2: {
    title: 'Problems',
    intro: 'Which problem do you face in your work, or have you identified in this topic? Write one thing at a time, in your own words.',
    hint: 'Write as many concrete things as you like.',
    placeholder: 'One concrete problem or observation…',
    start: 'Start',
    thinking: 'Thinking…',
    reply: 'Reply…',
    send: 'Send',
    refinedTitle: 'Suggested wording',
    useRefined: 'Use this',
    keepMine: 'Keep my wording',
    save: 'Save problem',
    saved: 'Saved',
    saving: 'Saving…',
    yours: 'Your problems',
    none: 'You have not saved any problems yet.',
    another: 'Write another problem',
    current: 'In progress',
    discard: 'Discard',
    error: 'Something went wrong',
  },
  systemicV2: {
    title: 'Problem mapping',
    subtitle: 'Obstacles to change, one lens at a time',
    loading: 'Loading…',
    error: 'Something went wrong',
    retry: 'Try again',
    back: 'Back',
    steps: {
      kipupiste: 'Problem',
      lens: 'Lens',
      dialogue: 'Conversation',
    },
    crumbs: { label: 'Steps', pending: 'choose…' },
    kipupistePicker: {
      title: 'Which problem are you taking on?',
      subtitle: 'Pick the one your group is working with.',
      empty: 'This workshop has no problems yet.',
      anchorCount: 'Obstacles to change: {{count}}',
    },
    kipupisteRail: {
      change: 'Change problem',
      changeLens: 'Change lens',
    },
    lensPicker: {
      title: 'Which lens are you looking through?',
      subtitle: 'The lens frames what kind of obstacles to change you look for.',
      empty: 'This workshop has no lenses defined.',
    },
    dialogue: {
      close: 'Close the conversation',
      title: 'What holds this in place?',
      opening: 'Tell us in your own words, or start from one of these.',
      ownWords: 'I will say it in my own words',
      suggestionsTitle: 'Or start here',
      placeholder: 'Write here…',
      send: 'Send',
      thinking: 'Thinking…',
      anchorsTitle: 'Obstacles to change found',
      anchorsEmpty: 'No obstacles yet — the conversation produces them.',
      accept: 'Save as obstacle to change',
      accepted: 'Saved',
      remove: 'Remove obstacle',
      removeConfirm: 'Remove the obstacle and the root causes derived from it?',
      removeYes: 'Remove',
      removeNo: 'Cancel',
      removeJudged: 'The obstacle can no longer be removed: the group has already judged its root causes.',
      finish: 'Done with this obstacle',
      finishHint: 'Once this obstacle is done you can change lens or problem.',
    },
    swapGuard: {
      title: 'Switch mid-conversation?',
      bodyLens: 'The conversation about this obstacle will be left unfinished. A new lens starts a new one.',
      bodyKipupiste: 'The conversation about this obstacle will be left unfinished. A new problem starts a new one.',
      confirm: 'Switch anyway',
      cancel: 'Keep talking',
    },
    chain: {
      holds: 'holds in place',
      seenThrough: 'seen through the lens',
    },
  },
  systemicMap: {
    title: 'Systemic map',
    nodeTypes: {
      v1: { kipupiste: 'Pain point', este: 'Blocker', juurisyy: 'Phenomenon', vipupiste: 'Leverage variable' },
      v2: { kipupiste: 'Problem', este: 'Obstacle to change', juurisyy: 'Root cause', vipupiste: 'Leverage point' },
    },
    loading: 'Loading…',
    refresh: 'Refresh',
    stages: {
      map: 'Map',
      kipupisteet: 'Pain points',
      blockers: 'Blockers',
      esteet: 'Blockers',
      loops: 'Loops',
    },
    empty: {
      title: 'Map is empty',
      body: 'Start by adding a pain point — an undesirable situation experts identify.',
      cta: 'Add first pain point',
    },
    deepen: {
      title: 'Deepen blocker',
      subtitle: 'Talk with AI toward concrete phenomena',
      placeholder: 'Reply or refine…',
      send: 'Send',
      start: 'Start deepening',
      loading: 'AI thinking…',
      candidates: 'Candidate nodes',
      accept: 'Add to map',
      editTitle: 'Edit title',
      agency: 'Agency',
      close: 'Close',
      doneHint: 'This blocker looks deep enough — you can close or continue.',
      openFromNode: 'Deepen with AI',
      error: 'Deepening failed',
    },
    addKipupiste: 'Add pain point',
    flowerList: {
      title: 'Pain points',
      orphans: 'Other nodes',
      noPetals: 'No blockers yet',
    },
    errors: {
      noInstance: 'Select a workshop instance (kid) to open the systemic map.',
      loadFailed: 'Could not load the systemic map.',
      esteEsteForbidden: 'Two blockers cannot connect directly — add a phenomenon between them.',
    },
    edgeModal: {
      title: 'Connection',
      proposed: 'AI proposal — confirm or edit',
      confirmed: 'Confirmed',
      contested: 'Contested',
      aiSuggested: 'AI',
      polarity: 'Effect',
      strengthens: '+ Strengthens',
      weakens: '− Weakens',
      strength: 'Strength',
      strengthWeak: 'Weak',
      strengthStrong: 'Strong',
      rationale: 'Rationale',
      rationalePlaceholder: 'Why does this connection work this way?',
      counterArgument: 'Alternative path',
      answerTest: 'Your answer to the test question (optional)',
      answerTestPlaceholder: 'How would you test this?',
      save: 'Save',
      confirm: 'Confirm proposal',
      reject: 'Remove connection',
      close: 'Close',
      spineHint: 'This is a primary layout link. Saving creates a typed causal edge on top of it.',
      saveSpine: 'Create typed connection',
      addBlocker: 'Add a blocker for this link',
    },
    loops: {
      title: 'Feedback loops',
      empty: 'No confirmed loops yet. Confirm edge polarities to detect reinforcing (R) and balancing (B) loops.',
      reinforcing: 'R',
      balancing: 'B',
      nodes: 'nodes',
      nameWithAi: 'Name with AI',
      naming: 'Naming…',
      leverageWithAi: 'Leverage with AI',
      leverageLoading: 'Finding leverage…',
      leverageTitle: 'Possible leverage points',
    },
    list: {
      title: 'List',
      nodes: 'Nodes',
      connections: 'Connections',
      loops: 'Loops',
      nodesEmpty: 'No factors on the map yet.',
      connectionsEmpty: 'No connections yet.',
      filtersLabel: 'Filter by node type',
    },
    duplicates: {
      title: 'Similar factor found',
      subtitle: '“{{existing}}” looks like the new “{{neu}}”. Use the existing one?',
      useExisting: 'Use existing',
      keepBoth: 'Keep both',
    },
    assumption: {
      reveal: 'Reveal assumption with AI',
      loading: 'Revealing…',
      label: 'Assumption',
      testQuestion: 'Test question',
    },
    critique: {
      title: 'Map critique',
      run: 'Map critique',
      loading: 'Analysing…',
      empty: 'No questions this time.',
      close: 'Close',
      check: 'Check with AI',
      checking: 'Critical AI is thinking…',
      errorTimeout: 'The check took too long. You can retry or add anyway.',
      errorFailed: 'The check failed. You can retry or add anyway.',
      dialogHint: 'Write the factor and press Check — AI challenges form, mechanism, and plausibility before saving.',
      checkFirstHint: 'Saving unlocks after the check. On error you can still add.',
      verdictReady: 'Ready for the map',
      verdictRefine: 'Worth refining',
      mechanism: 'Mechanism',
      relevance: 'Relevance',
      plausibility: 'Plausibility',
      suggestedRewrite: 'Suggested rewrite',
      applyRewrite: 'Use suggestion',
      applyRewriteAndAdd: 'Add with this wording',
      possibleDuplicate: 'Possible duplicate: “{{title}}”',
      replyLabel: 'Your reply to the AI',
      replyPlaceholder: 'Justify or refine…',
      sendReply: 'Send reply',
      edit: 'Edit',
      addAnyway: 'Add anyway',
      openQuestion: 'Open question',
      replyNow: 'Reply now',
      dialogueTitle: 'Critical dialogue',
      contestedFilter: 'Contested',
      contestedEmpty: 'No contested nodes or edges.',
      categories: {
        gap: 'Gap',
        hub: 'Hub',
        loop: 'Loop',
        contested: 'Contested',
        rationale: 'Rationale',
        dialogue: 'Dialogue',
        other: 'Other',
      },
    },
    blockers: {
      title: 'Blockers (resist change)',
      suggestAi: 'Suggest blockers with AI',
      loading: 'Asking AI…',
      empty: 'No suggestions yet. Run AI suggest, or add a blocker manually.',
      weakens: 'Weakens',
      accept: 'Add to map',
      dismiss: 'Dismiss',
      manualTitle: 'Add blocker manually',
      fieldTitle: 'Blocker',
      fieldDescription: 'Description',
      fieldTarget: 'What does it weaken?',
      pickTarget: 'Choose a pain point…',
      pickKipupiste: 'Select a pain point first for this blocker.',
      add: 'Add blocker',
      proposedTitle: 'Unconfirmed blockers',
      confirm: 'Confirm',
      reject: 'Reject link',
      scopeLabel: 'Can we influence this?',
      scopeEndogenous: 'We can influence this',
      scopeExogenous: 'Outside our control — constraint',
      fromEdgeHint: 'Blocker will be linked to the selected connection (weakens its target node).',
      votes: '{{count}} votes',
      vote: 'Vote',
      unvote: 'Remove vote',
    },
    addNode: {
      title: 'Add node',
      kipupisteTitle: 'Add pain point',
      esteTitle: 'Add blocker',
      ilmioTitle: 'Add phenomenon',
      from: 'Parent',
      text: 'Title',
      description: 'Description (optional)',
      save: 'Save',
      saveReady: 'Add to map',
      cancel: 'Cancel',
    },
    suggestConnections: {
      title: 'Suggested connections',
      subtitle: 'AI found possible links for “{{node}}”. Accept only if the rationale holds.',
      loading: 'Checking for possible connections…',
      empty: 'AI found no new connection suggestions for this node.',
      linkMeta: '{{polarity}} · confidence {{confidence}}%',
      bridgeVia: 'Needs a phenomenon between blockers: “{{title}}”',
      accept: 'Add as proposal',
      acceptBridge: 'Add phenomenon + links',
      dismiss: 'Dismiss',
      close: 'Close',
    },
    nodeModal: {
      title: 'Edit node',
      text: 'Text',
      description: 'Description',
      relations: 'Connections (click to edit)',
      noRelations: 'No connections yet. Add a consequence, link an existing node, or click an edge on the map.',
      spine: 'layout',
      starterHint: 'Scenario root — text can be edited; deletion is not allowed.',
      save: 'Save',
      addChild: 'Add consequence',
      addEste: 'Add blocker',
      addIlmio: 'Add phenomenon',
      addConnection: 'Link to existing',
      suggestConnections: 'Suggest connections (AI)',
      linkOther: 'Other factor',
      linkPick: 'Choose a node…',
      linkDirection: 'Direction',
      linkFromHere: 'From this → other',
      linkToHere: 'From other → this',
      linkPolarity: 'Effect',
      linkSave: 'Create connection',
      linkCancel: 'Cancel',
      linkNoneLeft: 'All other nodes are already linked to this one.',
      delete: 'Delete node',
      close: 'Close',
      scopeLabel: 'Blocker nature',
      votes: '{{count}} votes',
      vote: 'Vote',
      unvote: 'Remove vote',
    },
    layout: {
      arrange: 'Arrange',
      arrangeHint: 'Rearrange the map',
    },
    view: {
      flat: '2D',
      sphere: '3D',
      galaxy: 'Galaxy',
    },
    pan: {
      label: 'Move camera focus',
      forward: 'Pan forward',
      back: 'Pan backward',
      left: 'Pan left',
      right: 'Pan right',
      up: 'Pan up',
      down: 'Pan down',
      recenter: 'Center on origin',
    },
    guide: {
      title: 'How to use the systemic map',
      dismiss: 'Got it',
      mouse: {
        open: 'Hover a factor to preview, click to open.',
        loops: 'Hover a loop to trace it on the map, click to select.',
        navigate: 'Scroll to zoom, drag to pan.',
      },
      touch: {
        open: 'Tap a factor to highlight it, tap again to open.',
        loops: 'Tap a loop in the panel to trace it on the map.',
        navigate: 'Drag to pan, pinch to zoom.',
      },
    },
    legend: {
      title: 'Legend',
      starter: 'Scenario root',
      factor: 'Factor',
      blocker: 'Blocker',
      kipupiste: 'Pain point',
      este: 'Blocker',
      ilmio: 'Phenomenon',
      strengthens: '+ Strengthens',
      weakens: '− Weakens (dashed)',
      strengthWeak: 'Weak link (thin)',
      strengthStrong: 'Strong link (thick)',
      proposed: 'AI proposal (dashed)',
      loop: 'Loop (moving glow)',
    },
  },

  painPoints: {
    title: 'Pain point discovery',
    loading: 'Loading…',
    quickfireHint: 'Quickly add as many concrete issues as you like.',
    inputPlaceholder: 'One pain point or issue…',
    add: 'Add',
    allInputs: 'All inputs',
    yours: 'Yours',
    noInputs: 'No inputs yet.',
    clusters: 'Proposed pain points',
    runCluster: 'Cluster with AI',
    clustering: 'Clustering…',
    promote: 'Create kipupisteet on map',
    noClusters: 'No clusters yet — collect inputs and cluster.',
    members: 'members',
    drop: 'Drop',
    restore: 'Restore',
    editPrompt: 'Edit prompt',
    savePrompt: 'Save',
    cancel: 'Cancel',
    suggestWording: 'Suggest wording',
  },

  manualInputs: {
    saving: 'Saving…',
    locked: 'The workshop is locked. Unlock it before entering data by hand.',
    loading: 'Loading…',
    staticPivots: {
      title: 'Pivot points from a preset list',
      intro: 'Type or paste the pivot points, one per line. Add a description after a vertical bar: Title | description. Saving makes exactly these the preset pivot points; earlier preset pivot points stop being pivot points but are not deleted.',
      current: 'The instance has {{count}} pivot points now.',
      placeholder: 'Energy price | How price steers choices\nPublic transport',
      save: 'Save {{count}} pivot points',
      saved: 'Saved {{count}} pivot points.',
    },
    hmw: {
      title: 'What-if questions by hand',
      intro: 'Type the questions, one per line. Saving replaces the current list.',
      placeholder: 'What if…?',
      save: 'Save {{count}} questions',
      saved: 'Saved {{count}} questions.',
    },
    lensItems: {
      title: 'Obstacles and opportunities by hand',
      intro: 'Add an obstacle or opportunity to a pivot point through one of the six lenses. Edit and remove them in the Obstacles and opportunities view.',
      noPivots: 'The instance has no pivot points yet. Enter them first.',
      pivot: 'Pivot point',
      lens: 'Lens',
      type: 'Type',
      obstacle: 'Obstacle',
      opportunity: 'Opportunity',
      itemTitle: 'Title',
      add: 'Add',
      count: 'Obstacles and opportunities in total: {{count}}',
    },
  },

  navigation: {
    results: 'RESULTS',
    impactPath: 'Impact map',
    systemicMap: 'Systemic map',
    painPoints: 'Pain point discovery',
    v2Kipupisteet: 'Problems',
    v2Ankkurit: 'Obstacles to change',
    v2Juurisyyt: 'Root causes',
    impactPathScenarios: 'Scenarios',
    impactPathMap: 'Impact map',
    pivotPoints: 'Pivot points',
    ragSourceMap: 'Knowledge base',
    ragSourceMapV2: 'Knowledge base (v2)',
    dashboard: 'Dashboard',
    lenses: 'Barriers and opportunities',
    solutions: 'Solutions',
    report: 'AI summary',
    backToInstances: 'Back to instance selection',
    home: 'Home',
    userSettings: 'User settings',
    signOut: 'Sign out / switch user',
    signOutShort: 'Sign out',
    locked: 'Locked',
    moduleLocked: 'The workshop has not reached this yet',
    menuUnavailable: 'Could not load the menu: {{error}}',
    phasesOpen: 'Open the phases',
    phasesClose: 'Close the phases',
    phasesHeading: 'Kuutio phases',
    leaveWorkshopConfirm: 'Leave the workshop?',
  },

  drawer: {
    chat: 'Chat',
    settings: 'Settings',
    widthMode: {
      label: 'Panel width',
      normal: 'Normal',
      wide: 'Wide',
      full: 'Full width',
      next: 'Switch width: {{mode}}',
    },
  },

  tooltip: {
    dismissSession: "Don't show again",
    close: 'Close',
  },

  reportsList: {
    loading: 'Loading reports...',
    pageTitle: 'Kuutio reports',
    subtitle: 'Future workshop results — scenarios, impact paths, solutions, and AI analysis',
    empty: 'No published reports yet.',
    solutionsCount: '{{count}} solutions',
    transcriptsCount: '{{count}} speech items',
    participantsCount: '{{count}} participants',
  },

  screenClient: {
    loading: 'Loading...',
    selectDisplay: 'Select display',
    selectInstanceAndDisplay: 'Select instance and display',
    selectInstancePlaceholder: 'Select instance...',
    changeInstance: 'Change instance',
    selectInstanceFirstHint: '☝️ Select an instance above first',
    displaysSection: 'Displays',
    ipadsSection: 'iPads',
    wallRow: 'Wall {{id}} ({{name}})',
    wallNorth: 'North',
    wallEast: 'East',
    wallSouth: 'South',
    wallWest: 'West',
    screenTakeoverConfirm: 'Display {{code}} is already in use. Take over?',
  },

  workshopControls: {
    selectInstanceTitle: 'Select an instance first',
    selectInstanceBody: 'Go to the Admin home page to choose an instance.',
    adminHome: 'Admin home',
    pageTitle: 'Workshop controls',
    pageDescription:
      'For each scenario, choose which future image participants use in Impact Path. “Custom future” means participants start from a blank slate without predefined variables.',
    noScenariosTitle: 'No scenarios',
    noScenariosBody: 'This instance does not have any scenarios yet.',
    futuresAvailable: '{{count}} future images available',
    activeFutureLabel: 'Active future image',
    customFuture: 'Custom future image',
    freeBadge: 'Open',
    saving: 'Saving...',
    saved: 'Saved',
    noFuturesForScenario: 'No future images are defined for this scenario',
    topicFocusTitle: 'Topic focus',
    topicFocusDescription:
      'Choose the workshop focus. The selected perspective appears in Impact Path and guides AI analysis in all impact-path queries.',
    selectedPerspectiveLabel: 'Selected perspective',
    noPerspectiveAll: 'None selected — all perspectives',
    noFocusTitle: 'No focus',
    noFocusDescription: 'All perspectives are equal — no focus area',
  },

  workshopReport: {
    reportTitle: 'Report',
    workshopReportTitle: 'Workshop report',
    selectInstanceFirst: 'Select an instance from the Admin Hub to view the report.',
    backToAdmin: '← Back to Admin Hub',
    errorTitle: 'Error',
    retry: 'Try again',
    pageToolbarTitle: 'Report',
    generating: 'Generating...',
    refreshReport: 'Refresh',
    generateReport: 'Generate report',
    noReportYet: 'No report yet',
    noReportHint: 'Click “Generate report” to create a summary of this workshop.',
    generateBig: 'Generate report',
    generatingReport: 'Generating report...',
    summarySection: 'Summary',
    statParticipants: 'Participants',
    statSuggestions: 'Suggestions',
    statScenarios: 'Scenarios',
    moodDistribution: 'Mood distribution',
    moodPositive: 'Positive',
    moodNeutral: 'Neutral',
    moodConcerned: 'Concerned',
    chartsSection: 'Charts',
    chartScenarioActivity: 'Scenario activity',
    noData: 'No data',
    footerPlatform: 'Kuutio Workshop Platform',
    footerGenerated: 'Report generated {{date}}',
    loadError: 'Error loading data: {{message}}',
    unknownScenario: 'Unknown',
  },

  lenses: {
    pivotLabel: 'Pivot point',
    stepLenses: 'Obstacles and opportunities',
    navLenses: 'Lenses',
    navOverview: 'Overview',
    navPivot: 'Pivot',
    navEM: 'E&M',
    noInstance: 'No instance selected. Add a ?kid= or ?instance= URL parameter.',
    loadingData: 'Loading obstacles and opportunities...',
    loadErrorTitle: 'Error loading data',
    pivotSummaryNeedPivot: 'Select a pivot point before the summary.',
    pivotSummaryLoadFailed: 'Could not load the summary. Please try again.',
    pivotReportFailed: 'Could not generate the report. Please try again.',
    pivotSummaryBtn: 'Brief and analysis',

    // Dashboard tabs & chrome
    tabSummary: 'Summary',
    tabPivots: 'Pivot points ({{count}})',
    tabBrief: 'Brief',
    tabInnovations: 'Innovations',
    tabListing: 'Listing',
    itemsCount: '{{count}} items',
    obstacleOne: 'obstacle',
    obstacleOther: 'obstacles',
    opportunityOne: 'opportunity',
    opportunityOther: 'opportunities',
    manualCount: '{{count}} manual',
    aiCount: '{{count}} AI',
    ragCount: '{{count}} RAG',
    evaluatedTags: 'Evaluated (tags) {{count}}',
    qualityScore: 'Relevance {{score}}',
    researchSupport: 'Knowledge base {{score}}',
    pivotPointsSection: 'Pivot points ({{count}})',
    emptyItems: 'No items yet. Add obstacles and opportunities through the lenses.',
    noInstanceBrief: 'No instance — cannot load the brief.',
    noInstanceInnov: 'No instance — cannot load innovations.',
    unknownPivot: 'Unknown pivot point',
    briefFailed: 'Brief failed ({{status}})',
    innovFailed: 'Innovations failed ({{status}})',

    // Pivot summary
    formingPivotSummary: 'Generating pivot point summary...',
    lensCountsTitle: 'Counts by lens',
    obstaclesCountShort: '{{count}} obstacles',
    opportunitiesCountShort: '{{count}} opportunities',
    viewClusters: 'Clusters',
    viewLenses: 'Lenses',
    refreshClusters: 'Refresh clusters',
    refreshClusterAnalysis: 'Refresh cluster analysis',
    clustersStaleHint: 'Items have changed — a facilitator can refresh the cluster analysis.',
    formingClusters: 'Building clusters...',
    clusterFetchFailed: 'Cluster fetch failed ({{status}})',
    clustersFetchFailed: 'Could not fetch clusters',
    noItems: 'No items',
    obstacleClustersTitle: 'Obstacle clusters',
    opportunityClustersTitle: 'Opportunity clusters',
    clusterDefaultTitle: 'Cluster',
    unnamedItem: 'Untitled item',
    obstacles: 'Obstacles',
    opportunities: 'Opportunities',
    pivotAnalysis: 'Pivot analysis',
    membersCount: '{{count}} members',
    severityOptimism: 'Severity {{severity}} / 5 · Optimism {{optimism}} / 5',
    report: 'Report',
    reportNotCreated: 'No report has been created for this pivot point yet.',
    creating: 'Creating...',
    createReport: 'Create report',
    recreating: 'Recreating...',
    recreate: 'Recreate',

    // Simple / innovative / listing summaries
    formingBrief: 'Generating brief...',
    tryAgain: 'Try again',
    noItemsToSummarize: 'No lens items to summarize yet.',
    refresh: 'Refresh',
    keyFindings: 'Key findings',
    notableBlockersOpps: 'Notable obstacles and opportunities',
    fromCache: 'From cache (content matches current items).',
    pickingInnovations: 'Picking innovative angles...',
    noItemsToAnalyze: 'No lens items to analyze yet.',
    innovationsTitle: 'Innovations and surprises',
    noHighlights: 'No highlights returned.',
    related: 'Related:',
    fromCacheShort: 'From cache.',
    listing: 'Listing',
    noItemsToList: 'No items to list.',
    groupByLens: 'Group by lens',
    groupByType: 'Group by type',
    obstacle: 'Obstacle',
    opportunity: 'Opportunity',

    // Item modal
    editObstacle: 'Edit obstacle',
    editOpportunity: 'Edit opportunity',
    addObstacle: 'Add obstacle',
    addOpportunity: 'Add opportunity',
    obstaclePrompt: 'What blocks or slows down realizing pivot point "{{title}}"{{lensSuffix}}?',
    opportunityPrompt: 'What opportunity opens at pivot point "{{title}}"{{lensSuffix}}?',
    lensSuffix: ' from a {{lens}} perspective',
    writeObstacle: 'Write an obstacle...',
    writeOpportunity: 'Write an opportunity...',
    saveLocked: 'The workshop is locked, so this can’t be saved now. Your text is kept in this window.',
    saveFailed: 'Saving failed: {{error}}. Your text is kept in this window.',
    findObstaclesOpps: 'Find obstacles and opportunities',
    eachLensPerspective: 'Each lens brings its own perspective',
    letsStart: 'Let’s start!',

    // Pivot curation (post–Let's go)
    curation: {
      title: 'Team pivot points',
      empty: 'No pivot points',
      add: 'Add pivot point',
      selectTitle: 'Select this pivot point',
      selectionHint: 'Select the pivot points to work on in the next stages · {{selected}}/{{total}} selected',
    },
    editModal: {
      createTitle: 'Add pivot point',
      editTitle: 'Edit pivot point',
      titleLabel: 'Title',
      titlePlaceholder: 'Pivot point title...',
      descriptionLabel: 'Description',
      descriptionPlaceholderCreate: 'Short description of the pivot point...',
      descriptionPlaceholderEdit: 'Description...',
      generateAi: 'Generate with AI',
      generating: 'Generating...',
      regenerate: 'Regenerate',
      generateTitle: 'Generate description with AI',
      replaceTitle: 'Replace current AI text',
      aiHint: 'AI-generated — edit freely',
    },
    pivotPointsNav: 'Pivot points',
    lensFallback: 'Lens',
    aiObstacles: 'AI obstacles',
    aiOpportunities: 'AI opportunities',
    emptyLensDetail: 'Add obstacles and opportunities for this pivot point from the "{{lens}}" lens perspective.',
    emptyLensDetailAi: 'Or let AI suggest them based on research.',
    emptyNoItemsYet: 'No obstacles or opportunities yet',
    aiSuggestionsTitle: 'AI: {{type}}',
    aiAnalyzingResearch: 'Analyzing research material...',
    aiAnalyzingHint: 'This takes a moment — AI is going through the research database',
    aiSuggestFailed: 'Could not create AI suggestions.',
    noSuggestions: 'No suggestions available.',
    suggestMore: 'Suggest more',
    adding: 'Adding...',
    addSelected: 'Add selected ({{count}})',
    aiSuggestsObstacles: 'AI suggests obstacles',
    aiSuggestsOpportunities: 'AI suggests opportunities',
    editBeforeAdd: 'Edit as needed before adding',
    selectAndEditBeforeAdd: 'Select and edit before adding',
    aiAnalyzingGenerating: 'AI is analyzing and generating suggestions...',
    aiCouldNotGenerate: 'AI could not generate suggestions.',
    tryAgainOrManual: 'Try again or add manually.',
    aiSelectorHint: 'AI suggestions are grounded in research. Select what you want to keep, edit if needed, then add.',
    descriptionPlaceholder: 'Description...',
    edited: 'edited',
    confidence: 'Confidence: {{pct}}%',
    selectedCount: 'Selected: {{selected}} / {{total}}',
    sourceManual: 'Manual',
    sourceAiGenerated: 'AI-generated',
    sourceResearch: 'Research material',
    tabDetails: 'Details',
    tabConnections: 'Connections ({{count}})',
    tabSources: 'Sources ({{count}})',
    metadata: 'Metadata',
    aiConfidence: 'AI confidence',
    addedBy: 'Added by',
    relatedItems: 'Related obstacles and opportunities',
    noRelatedItems: 'No related items.',
    own: 'Own',
    aiAnalyzing: 'AI analyzing...',
    untitled: 'Untitled',
    requestFailed: 'Request failed',
    clustersRefreshFailed: 'Could not refresh clusters',
    deleteItemTitle: 'Delete item',
    deletePivotTitle: 'Delete pivot point',
    deleteConfirm: 'Delete "{{title}}"? This cannot be undone.',
    matrixTitle: 'Pivot × Lens matrix',
    totalShort: 'Tot.',
    total: 'Total',
    lensDistribution: 'Lens distribution',
  },

  solutionAnalysis: {
    title: 'Analysis and evaluation',
    swot: {
      strengths: 'Strengths',
      weaknesses: 'Weaknesses',
      opportunities: 'Opportunities',
      threats: 'Threats',
    },
    swotTitle: 'SWOT analysis',
    nextSteps: {
      title: 'What\'s next',
      universal: 'For every town',
      town: 'For you · {{town}}',
    },
    lensesTitle: 'Lenses',
    lensesAssessed: '{{count}} lenses assessed',
    coverageTitle: 'Lens coverage',
    coverageAddressed: '{{count}} covered',
    coverageAnswered: '{{count}} barriers answered',
    coverageOpen: '{{count}} open barriers',
    eval: {
      impact: "Impact",
      feasibility: "Feasibility",
      innovation: "Innovation",
      evidence: "Research evidence",
    },
    solutionsTitle: "Solutions",
    solutionsIntro: "The workshop groups developed {{count}} solution proposals for the research question.",
    hybridTitle: "Combined solutions",
    hybridIntro: "The AI analysed the {{count}} workshop solution proposals and identified combinations where proposals from several groups complement each other.",
  },
  instanceSettingsModal: {
    modalTitle: 'Instance settings',
    modalSubtitle: 'Edit this Kuutio instance and manage its state.',
    successSaved: 'Changes saved successfully.',
    statsHeading: 'Statistics',
    statParticipants: 'Participants',
    statPatterns: 'Patterns',
    statDays: 'Days',
    labelName: 'Name',
    labelDescription: 'Description',
    labelWorkshopGoal: 'Workshop goal',
    placeholderWorkshopGoal: 'E.g. What needs to happen for employers to hire young people?',
    helpWorkshopGoal: 'The question this workshop is answering. The AI is steered to find pivot points and evaluate solutions against this goal. Required field.',
    workshopGoalRequired: 'Workshop goal is required — the question this workshop is answering.',
    labelAiContextDescription: 'AI context description',
    helpAiContextDescription: 'Description of Kuutio and this workshop, injected into AI prompts. Edit the default text directly. Leave it unchanged to keep using the shared default.',
    langFi: 'Finnish',
    langEn: 'English',
    langSv: 'Finland Swedish (sv-FI)',
    labelStatus: 'Status',
    labelType: 'Type',
    typeLive: 'Live — in-person workshop (iPads)',
    typeOnline: 'Online — remote participants',
    googleSlidesLabel: 'Google Slides presentation',
    googleSlidesHelp: 'Paste the Google Slides URL or ID. Used for workshop slides.',
    slideImagesLabel: 'Slide images',
    syncImages: 'Sync images',
    syncingImages: 'Syncing...',
    imagesSyncedProgress: '{{synced}}/{{total}} images synced',
    syncedCount: '{{count}} synced',
    skippedCount: '{{count}} skipped',
    errorsCount: '{{count}} errors',
    sheetSyncLabel: 'Sync from Google Sheet',
    sheetSyncHelp: 'Import scenarios, variables, and flow steps from a Google Sheet.',
    loading: 'Loading...',
    preview: 'Preview',
    sync: 'Sync',
    syncing: 'Syncing...',
    previewBoxTitle: 'Preview',
    sheetInstance: 'Instance:',
    sheetScenarios: 'Scenarios:',
    sheetVariables: 'Variables:',
    replaceConfigWarning: 'This replaces the current configuration',
    aiSuggestLabel: 'AI suggestions (barriers / opportunities)',
    aiSuggestHint: 'How many AI suggestions to create per lens (default 1)',
    testModeLabel: 'Test mode',
    openPinTitle: 'Open PIN',
    openPinDesc: 'Any PIN is accepted (testing / demos)',
    lockLabel: 'Lock',
    lockTitle: 'Lock instance',
    lockDesc: 'Prevent edits in all views (impact path, pivot points, lenses)',
    reportVisibilityLabel: 'Report visibility',
    implementationTitle: 'Show “How do we start?” sections',
    implementationDesc:
      'AI-generated 12-month implementation plans appear with solutions in the report',
    scenariosHeading: 'Scenarios',
    scenarioNamePlaceholder: 'Scenario name',
    scenarioDescPlaceholder: 'Description (optional)',
    hideScenario: 'Hide',
    showScenario: 'Show',
    deleteScenario: 'Delete',
    deleteScenarioContent: 'The scenario\'s impact path has {{count}} nodes or blockers added by participants. They are deleted with the scenario when you save.',
    addScenario: 'Add scenario',
    clearDataTooltip: 'Clear all participant input (keeps the instance)',
    clearData: 'Clear data',
    deleteInstance: 'Delete instance',
    saveChanges: 'Save changes',
    savingChanges: 'Saving...',
    clearConfirmTitle: 'Confirm clearing data',
    clearConfirmP1: 'Clear all participant input from instance "{{title}}"?',
    clearConfirmP2:
      "This removes what the workshop produced, its votes and AI output, and the workshop's own RAG chunks, and the flow returns to the start. Kept: scenarios, variables, lenses, settings, PINs, participants, the knowledge base (research material) and the participants' pre-work.",
    clearPreviewHeading: 'Will remove {{count}} rows:',
    clearPreviewNothing: 'Nothing to remove.',
    clearPreviewFailed: 'The preview failed: {{error}}',
    clearing: 'Clearing...',
    deleteConfirmTitle: 'Confirm deletion',
    deleteConfirmP1:
      'Permanently delete instance "{{title}}"? This removes all related data.',
    deleting: 'Deleting...',
    deletePermanently: 'Delete permanently',
    errorSyncFailed: 'Sync failed',
    errorPreviewFailed: 'Preview failed',
    errorSheetSyncFailed: 'Sync failed',
    confirmSheetReplace: 'This replaces all current scenarios, variables, and flow steps. Continue?',
    syncDoneAlert: 'Synced! {{scenarios}} scenarios, {{variables}} variables',
    syncStart: 'Starting...',
    syncSyncingImages: 'Syncing {{total}} images...',
    syncLoadingImage: 'Loading image {{current}}/{{total}}',
    syncRetryRound: 'Retry {{round}}/{{maxRounds}} ({{failedCount}} failed)',
    syncRetryingSlide: 'Retrying {{slideIndex}}/{{total}} (round {{round}})',
    timeJustNow: 'just now',
    timeMinutesAgo: '{{count}} min ago',
    timeHoursAgo: '{{count}} h ago',
    timeDaysAgo: '{{count}} d ago',
    newScenarioTitle: 'New scenario',
    statusReadOnlyHint: 'Change in the Workshop view',
    pinLabel: 'Instance PIN code',
    pinHelp: 'Participants use this PIN to sign in directly to this instance.',
    lensesSectionLabel: 'Lenses (barriers & opportunities)',
    lensDescriptionTitle: 'Also generate description',
    lensDescriptionDesc: 'AI generates a description text in addition to the title for barriers and opportunities.',
    devSectionLabel: 'Development',
    qaToggleTitle: 'QA testing',
    qaToggleDesc: 'Shows the QA panel in all admin views. Saved on this device only.',
  },

  flowBuilder: {
    title: 'Workshop flow',
    subtitle: '{{instance}} · builder',
    save: 'Save',
    cancel: 'Cancel',
    unsaved: 'Unsaved changes',
    allSaved: 'All saved',
    fromTemplate: 'Start from a template…',
    fromInstance: 'Copy from an instance…',
    liveBanner: 'You are editing a running workshop',
    liveNow: 'Running now: {{module}}. Saved changes reach participants immediately.',
    readinessOk: 'Readiness: everything needed is available',
    readinessManual: '{{count}} to enter by hand',
    readinessOrder: '{{count}} order warning(s)',
    readinessYellow: '{{count}} by hand or optional',
    readiness: 'Readiness',
    palette: 'Modules',
    paletteHint: 'Drag into the flow, or click to see the description.',
    inChain: 'In the flow',
    producesAlready: 'Overlapping module: {{module}}',
    chain: 'Flow',
    tieto: 'Data',
    alwaysAvailable: 'Always available',
    reportFixed: 'Always last',
    noInputs: 'Needs no earlier data',
    optional: 'optional',
    statusUpstream: 'available',
    statusSatisfied: 'available',
    statusPreseeded: 'by hand',
    statusOrder: 'too late',
    statusManual: 'missing',
    statusOptional: 'not available',
    enterByHand: 'Enter by hand',
    unsavedTag: 'Unsaved',
    liveTag: 'Running',
    contentTag: 'Has content',
    moveUp: 'Move up',
    moveDown: 'Move down',
    remove: 'Remove from the flow',
    removeNote: 'Removing deletes no data – the module\'s content just stops being shown. The change is saved only with Save.',
    addToChain: 'Add to the flow',
    swapVersion: 'Switch to this version',
    showInChain: 'Show in the flow',
    conflictSameFamily: '{{other}} is already in the flow and produces the same data. The flow can hold only one version.',
    conflictOther: '{{other}} already produces {{handoff}}. Choose one of them.',
    needs: 'Needs',
    canUse: 'Uses if available',
    produces: 'Produces',
    facilitationTitle: 'For the facilitator',
    facilitationWhat: 'What?',
    facilitationWhy: 'Why?',
    facilitationHow: 'How?',
    facilitationEmpty: 'Not written yet',
    participantModule: 'Participants',
    facilitatorOnly: 'Facilitator only',
    openInWorkshop: 'Open in workshop control',
    enterOnContent: 'Entered on the Content page under Preparation →',
    prepareSettings: 'Preparation phase settings',
    liveSettings: 'Workshop phase settings',
    noSettings: 'No settings',
    liveSwitches: 'Switches',
    liveActions: 'Actions',
    livePanel: 'Control panel',
    prepareAfterAdd: 'Editable once the module is added to the flow.',
    tietoPreseeded: 'Entered by hand',
    tietoMissing: 'Missing – to enter by hand',
    tietoOptionalMissing: 'Not available – optional',
    tietoUnused: 'Not used in the flow',
    consumers: 'Used by: {{list}}',
    hasContent: 'Has content',
    noContent: 'No content yet',
    storage: 'Stored in',
    producer: 'Produced by',
    status: 'Status',
    content: 'Content now',
    noProducer: 'No module in the flow',
    editorsTitle: 'Enter by hand',
    editorsBlocked: 'Save the flow changes first – data entered by hand is saved at once and is not part of the draft.',
    editorWarnDestructive: 'Replaces the current content',
    editorsNote: 'Data entered by hand can also pre-fill data a later module produces – that module continues from it.',
    editorsOpenInWorkshop: 'The editors open in workshop control at that module.',
    templatesTitle: 'Start from a template',
    templatesNote: 'A template replaces the draft\'s flow. Nothing is saved before Save, and no data is deleted.',
    templateSuggests: 'Suggests entering by hand: {{list}}',
    copyTitle: 'Copy from an instance',
    copyNote: 'The selected instance\'s flow goes into the draft. Nothing is saved before Save.',
    confirmTitle: 'Save the flow changes?',
    confirmLive: 'The workshop is running ({{module}}). Changes reach participants as soon as they are saved.',
    confirmStarted: 'The workshop has already started.',
    confirmRemoved: 'Removing from the flow: {{module}}.',
    confirmRemovedContent: 'Removing from the flow: {{module}} – its content stays, it just stops being shown.',
    confirmRemovedLive: 'The running module {{module}} is removed – the flow moves on to the next module.',
    confirmAdded: 'Adding to the flow: {{module}}.',
    confirmReordered: 'The order changes.',
    confirmLiveMoved: 'The running module moves.',
    confirmSave: 'Save changes',
    confirmBack: 'Back to editing',
    leaveConfirm: 'You have unsaved changes to the flow. Leave anyway?',
    saved: 'Saved.',
    saveFailed: 'Could not save: {{error}}',
    stale: 'Someone else saved the flow in between. Reload – the changes in your draft will be lost.',
    reload: 'Reload',
    close: 'Close',
    shortcuts: 'Keys: ↑ / ↓ move the selected module · Delete removes · Ctrl+S saves · Esc closes',
    loadFailed: 'Could not load the flow: {{error}}',
    noInstance: 'Select an instance first.',
    openBuilder: 'Edit the flow',
    drag: 'Drag to move',
    view: 'View',
    viewStatic: 'Diagram',
    viewDynamic: 'Dynamic',
    viewTimeline: 'Timeline',
    groupSocial: 'Social',
    addAgain: 'Add again',
    timelineStart: 'Kuutio starts',
    timelineTotal: 'Total {{hours}} h {{minutes}} min',
    timelineSavedOnly: 'The timeline shows the saved flow — save your changes to see them here.',
    timelineDay: 'Day {{day}}',
    timelineMin: 'min',
    timelineDayBreak: 'Start a new day from this step',
    timelineAnchor: 'Set an exact start time',
    timelineAnchorHint: 'This step starts exactly at:',
    timelineAnchorClear: 'Remove start time',
    timelineNoStart: 'Set the Kuutio start time to compute a clock time for every step.',
    timelineEnd: 'The Kuutio ends',
    legendIn: 'Input',
    legendOut: 'Output',
    reportUsesThis: 'The report uses this',
    reportUsesCount: 'The report uses {{count}} data',
    reportSections: 'Report sections',
    groupStart: 'Future',
    groupMapping: 'Mapping',
    groupPivots: 'Pivot points',
    groupWork: 'Lenses and solutions',
    groupSystemicV2: 'Problem mapping',
    groupForesight: 'Scenarios',
  },
  moduleFacilitation: {
    title: 'Facilitation',
    lead: 'The modules\' shared facilitation texts – the base every Kuutio\'s script starts from. Changes apply to every Kuutio and are saved as versions. A Kuutio\'s own changes are made in its script.',
    search: 'Search modules',
    selectModule: 'Select a module on the left',
    noMatches: 'No matches.',
    loadFailed: 'Loading failed: {{error}}',
    group: { work: 'Work phases', social: 'Social' },
    minutes: '{{count}} min',
    editedBadge: 'Edited · v{{version}}',
    manifestBadge: 'Original',
    finnishOnly: 'You are editing the Finnish texts. Translations are kept as they are.',
    field: {
      what: 'What',
      why: 'Why',
      how: 'How',
      intro: 'Introduction (suggested wording)',
      technicalSetup: 'Technical setup',
      recommendedDuration: 'Recommended duration (min)',
      steps: 'Run of play',
    },
    stepsTotal: 'Steps total {{minutes}} min',
    stepTitle: 'Title',
    stepMinutes: 'Min',
    stepText: 'What happens',
    addStep: 'Add step',
    removeStep: 'Remove step',
    moveUp: 'Move up',
    moveDown: 'Move down',
    versionName: 'Version name',
    versionNameHint: 'e.g. after K9',
    changeNote: 'What changed',
    save: 'Save',
    cancel: 'Cancel',
    saved: 'Saved as version {{version}}.',
    saveFailed: 'Saving failed: {{error}}',
    history: 'Version history',
    liveVersion: 'Current · v{{version}}',
    manifestLive: 'Current · original text',
    manifestVersion: 'original text',
    noVersions: 'No earlier versions.',
    preview: 'Show',
    rename: 'Rename',
    restore: 'Restore',
    restoreTitle: 'Restore version {{version}}?',
    restoreMessage: 'The version is restored as a new current version. Nothing is lost: the current text stays in the version history.',
    restoredNote: 'Restored version {{version}}',
    restored: 'Version {{version}} restored; the current one is now v{{live}}.',
    emptyText: 'No text.',
  },
  scriptView: {
    title: 'Script',
    hint: 'Every step of the Kuutio with its facilitation texts. Edits apply to this Kuutio only; the modules\' shared base is refined separately.',
    edit: 'Edit for this Kuutio',
    noInstance: 'Choose a Kuutio first.',
    loadFailed: 'Loading the flow failed: {{error}}',
    print: 'Print',
    editTimes: 'Edit times (Flow)',
    noStart: 'The Kuutio start time is not set, so clock times are missing. Set it on the Flow timeline.',
    day: 'Day {{day}}',
    minutes: '{{count}} min',
    what: 'What',
    why: 'Why',
    intro: 'Introduction',
    technicalSetup: 'Technical setup',
    how: 'How',
    runOfPlay: 'Run of play',
    notes: 'Notes',
    customized: 'for this Kuutio',
    noContent: 'No facilitation content written yet.',
    end: 'The Kuutio ends',
  },
  facilitationPanel: {
    title: 'Facilitation',
    customizedCount: '{{count}} customized for this Kuutio',
    hint: 'Grey text is the module\'s shared base. Write here when this Kuutio needs its own — the base stays as it is, and the change shows in no other Kuutio.',
    what: 'What',
    why: 'Why',
    intro: 'Introduction (suggested wording)',
    how: 'How',
    technicalSetup: 'Technical setup',
    notes: 'Own notes',
    customized: 'customized for this Kuutio',
    reset: 'Restore the base',
    noBase: 'No base written yet.',
    steps: 'Run-of-play',
    customizeSteps: 'Edit the run-of-play for this Kuutio',
    noBaseSteps: 'The base has no run-of-play.',
    stepsTotal: 'Steps in total {{minutes}} min',
    stepTitle: 'Title',
    stepMinutes: 'Min',
    stepText: 'What happens',
    addStep: 'Add a step',
    removeStep: 'Remove the step',
    moveUp: 'Move up',
    moveDown: 'Move down',
  },
  facilitationChat: {
    title: 'Improve with AI',
    close: 'Close the conversation',
    hintBase: 'The AI reviews the texts and proposes improvements. An accepted proposal goes into the field; save a version as usual. The conversation is not stored and is lost when you switch module.',
    hintInstance: 'The AI proposes improvements to this Kuutio\'s texts. An accepted proposal is saved for this Kuutio at once; the base stays as it is. The conversation is not stored.',
    emptyHint: 'You can leave the field empty: the AI then reviews the texts and proposes general improvements.',
    generalRequest: 'General improvements, please.',
    original: 'Original',
    originalEmpty: '(empty)',
    originalNoSteps: '(no steps)',
    placeholder: 'Say what you want to improve…',
    send: 'Send',
    progress: {
      reading: 'The AI is reading the texts…',
      thinking: 'The AI is thinking about improvements…',
      writing: 'The AI is writing proposals…',
    },
    proposalsTitle: 'Proposals',
    proposalsEmpty: 'No proposals yet.',
    accept: 'Accept',
    accepted: 'Accepted',
    stepsProposal: 'Proposed run-of-play',
    stepsReplace: 'Accepting replaces the whole run-of-play.',
    stepsWipeTranslations: 'The steps\' translations (en/sv) will be cleared.',
    minutes: '{{count}} min',
    error: 'The conversation failed: {{error}}',
    cutOff: 'The answer was cut off.',
    lockedNote: 'The Kuutio is locked, so proposals cannot be accepted.',
  },
  workshopOrchestrator: {
    screenStateDeleteConfirm: 'Delete this screen state?',
    pathwayImportReplaceConfirm: 'The map already has {{count}} nodes. The import replaces the instance\'s trends, variables and the whole impact map with this JSON. Continue?',
    controlLockedReached: 'Locked: the workshop has already reached this step, so the choice can no longer change.',
    loading: 'Loading…',
    selectInstanceTitle: 'Select an instance first',
    selectInstanceBody: 'Go to the admin home page to pick an instance.',
    adminHome: 'Admin home',
    tabContent: 'Content',
    tabScreens: 'Screens',
    lockedBar: 'Workshop locked — edits are blocked until you unlock it.',
    unlockedBar: 'Workshop editable. Lock it to prevent accidental changes.',
    lock: 'Lock',
    unlock: 'Unlock',
    lockFailed: 'Could not update the lock: {{error}}',
    unlockTitle: 'Unlock the workshop?',
    unlockMessage: 'This allows edits again. Lock it again as soon as you are done so accidental changes do not get through.',
    notStartedTitle: 'The workshop has not started',
    notStartedBody: 'You can prepare the modules now. Start when the room is ready: the walls show the Kuutio cube first.',
    start: 'Start the workshop',
    startFailed: 'Could not start: {{error}}',
    chainMissing: 'This instance has no workshop flow.',
    chainLoadFailed: 'Could not load the workshop flow: {{error}}',
    manualNeededBar: 'Enter by hand before starting: {{items}}',
    orderWarningBar: 'Order warning: {{items}} is produced only later in the flow.',
    navAria: 'Modules',
    setup: 'Preparation',
    stepOf: 'Module {{current}} / {{total}}',
    gate: {
      ok: 'Checks satisfied',
      unmet: '{{count}} pending',
      open: 'Open the step\'s controls',
    },
    moduleActions: {
      neverRun: 'Not run yet',
      running: 'Running',
      done: 'Done',
      failed: 'Failed',
      errors: '{{count}} errors',
      leaveTitle: 'Before you continue',
      continueAnyway: 'Continue anyway',
      cancel: 'Cancel',
      checkUnreadable: 'The check could not be read: {{detail}}',
      signin: 'The module\'s actions need a Sitra sign-in.',
    },
    previous: 'Previous',
    next: 'Next',
    pause: 'Pause',
    resume: 'Resume',
    timerPlus: 'Add 5 minutes',
    timerMinus: 'Remove 5 minutes',
    stop: 'Stop the workshop',
    live: 'LIVE',
    state: {
      ready: 'Output ready',
      missing: 'Output missing',
      checksOk: 'Checks satisfied',
    },
    noSettingsTitle: 'No facilitator settings',
    noSettingsBody: 'This module needs no separate settings from the facilitator.',
    manualTitle: 'Entered by hand',
    manualBody: 'This module needs data that no earlier module produces. Enter it by hand before the module starts.',
    manualDone: 'entered by hand',
    orderWarning: '{{handoff}} is produced only later ({{producer}}).',
    noEditor: 'There is no editor for this data yet.',
    openEditor: 'Enter by hand',
    closeEditor: 'Close',
    enterOnContent: 'Enter it on the Content page',
    blockedUntil: 'The module content opens once {{items}} has been entered.',
    settingsTitle: 'Screens and timing',
    duration: 'Duration (min)',
    slideStart: 'First slide',
    slideEnd: 'Last slide',
    access: 'Participants',
    accessAuto: 'Opens when the flow reaches the module',
    accessOpen: 'Always open',
    accessLocked: 'Locked',
    screenStates: 'Screen states: {{count}}',
    editScreens: 'Edit screens',
    saved: 'Saved',
    saveFailed: 'Could not save: {{error}}',
    chainTitle: 'Workshop flow',
    chainHint: "This instance's modules in order. Modules and order are edited in the builder; preparation, timing and data entered by hand: Workshop control.",
    openOrchestrator: 'Open workshop control',
  },
  adminHome: {
    stage_prepare: 'Preparation',
    stage_run: 'Running',
    stage_results: 'Results',
    currentStage: 'Now',
    nextStep: 'Next step',
    moveTo_run: 'Move to running',
    moveTo_results: 'Move to results',
    stageDone: 'All done.',
    allViews: 'All views',
    legacy: 'legacy',
    health: 'Service status',
    backend: 'Backend',
    database: 'Database',
    rag: 'Knowledge search',
    view_impactPath: 'Impact map',
    view_pivotPoints: 'Pivot points',
    view_lenses: 'Obstacles and opportunities',
    view_solutions: 'Solutions',
    view_report: 'Public report',
    view_impactMachine: 'Impact Machine',
    view_postit: 'PostIt',
    view_weather: 'Weather',
    view_statmap: 'Statistics map',
  },
  stageStatus: {
    goal: 'Workshop goal written',
    language: 'Language: {{language}}',
    flow: 'Flow composed: {{modules}} modules',
    inputs: 'Module inputs: {{missing}} missing',
    scenarios: 'Scenarios: {{count}}',
    access: 'Access: workshop PIN or {{count}} personal codes',
    datasets: 'Knowledge base: {{count}} datasets, {{pending}} awaiting review',
    slides: 'Slide deck attached',
    organisations: 'Organisations imported: {{count}}',
    started: 'Workshop started',
    progress: 'In the flow: phase {{current}}/{{total}}',
    devices: 'Device connections',
    report: 'Report created',
    translations: 'Translations: {{languages}}',
    transcripts: 'Transcripts: {{count}}',
    whatif: 'What if? texts: {{count}}',
    summary: 'Executive summary',
    export: 'Export and archiving',
    optional: 'optional',
    byScript: 'by script',
  },
  stageRow: {
    live: 'Workshop running',
    stillRunning: 'The workshop continues; screens stay on the step',
    step: 'step {{current}}/{{total}}',
    backToWorkshop: 'Back to the Workshop',
  },
  prep: {
    title: 'Module inputs',
    beforeTitle: 'Before the Kuutio',
    duringTitle: 'Decided during the Kuutio (Workshop)',
    required: 'required',
    optional: 'optional',
    sameAsAbove: 'Same as above.',
    preparedAbove: 'Scenarios and variables, at the top of this page',
    knowledge: 'on the Knowledge base page',
    system: 'Created automatically',
    workshop: 'Produced during the Kuutio by {{module}}',
    none: 'Cannot be entered in advance.',
    noneRequired: 'Required, but there is no way to enter it in advance: only by script or in the database. Add a module to the flow that produces it.',
    noInputs: 'No inputs.',
    noDecisions: 'No facilitator decisions during the Kuutio.',
    panel: 'The module\'s own facilitator panel',
    noChain: 'No modules have been chosen for the flow yet.',
    openFlow: 'Compose the flow',
  },
  scenarioVariables: {
    noFuture: 'Without a future image',
    groupCount: '{{count}} variables',
    sectionTitle: 'Scenarios and variables ({{count}})',
    title: 'Variables ({{count}})',
    name: 'Variable',
    description: 'Description',
    future: 'Future image',
    add: 'Add variable',
    delete: 'Delete variable',
    deleteConfirm: 'Delete the variable {{name}}?',
    newName: 'New variable',
    toggle: 'Variables',
    saveFirst: 'Save the scenario first to add variables.',
  },
  scenarioCopy: {
    label: 'Copy from another Kuutio:',
    pick: 'Choose a Kuutio…',
    count: '{{count}} scenarios',
    button: 'Copy scenarios and variables',
    done: 'Copied {{scenarios}} scenarios and {{variables}} variables.',
    confirmReplace: 'This Kuutio already has {{count}} impact map nodes. Copying replaces the variables of scenarios with the same name and hides the others. Continue?',
  },
  instanceList: {
    title: 'Instance management',
    lead: 'Every cube. Open takes you to the cube\'s overview, where its stages and settings are.',
    create: 'Create a new cube',
    search: 'Search by name or description',
    filter_archived: 'Archived',
    filter_all: 'All',
    loadFailed: 'Could not load instances: {{error}}',
    retry: 'Try again',
    empty: 'No cubes yet. Create the first one.',
    noMatches: 'No cubes match the search.',
    participants: 'Participants',
    participantCount: '{{count}} participants',
    workshopDate: 'Workshop date',
    open: 'Open',
    filter_current: 'Current',
    archive: 'Archive',
    archiveHint: 'An archived cube leaves every menu and shows only under the Archived filter on the Instances page. It can be restored.',
    archivedHelp: 'This cube is archived: it is not shown in menus. Restore brings it back.',
    archivedOn: 'Archived {{date}}',
    restore: 'Restore',
    archived: '"{{title}}" was archived.',
    restored: '"{{title}}" was restored.',
    deleted: '"{{title}}" was deleted ({{count}} rows).',
  },
  instanceLabels: {
    title: 'Labels',
    help: 'Choose at least one. The Instances list is filtered by label.',
    required: 'Choose at least one label for the cube.',
    filterBy: 'Label:',
    none: 'No label',
    edit: 'Edit labels',
    kuutio: 'Kuutio',
    kuutio_hint: 'A real workshop with real participants',
    demo: 'Demo',
    demo_hint: 'For showing to visitors',
    simulation: 'Simulation',
    simulation_hint: 'A run with AI personas',
    test: 'Test',
    test_hint: 'A technical check or a throwaway copy',
    lab: 'Lab',
    lab_hint: 'Trying a new module on real data or a real session',
    event: 'Event',
    event_hint: 'A one-off session, not a full cube',
  },
  instanceDelete: {
    title: 'Delete the cube permanently',
    lead: 'The cube "{{title}}" and all its data will be deleted permanently. This cannot be undone.',
    previewHeading: '{{count}} rows will be deleted:',
    previewFailed: 'Preview failed: {{error}}',
    typeTitle: 'Confirm by typing the cube\'s name: {{title}}',
  },
  settingsPages: {
    keyActorsTitle: 'Key actors',
    keyActorsLead: 'Who this workshop reaches: who is in the room, or can be reached through those who are.',
    keyActorsLabel: 'One actor per line',
    keyActorsPlaceholder: 'E.g. a ministry, a municipal council or an organisation',
    keyActorsHelp: "The Problem mapping vipupisteet read this list with the workshop description: when one of these could change an instrument, it is within the workshop's mandate. Left empty, the AI judges the mandate from the description.",
    keyActorsSave: 'Save actors',
    keyActorsSaved: 'Actors saved',
    webSearchTitle: 'Web search',
    webSearchLead: 'The summary analyses can also look things up on the web, from vetted publishers.',
    webSearchLabel: 'Web search for the summary analyses',
    webSearchHelp: 'The problem map and solutions summary analyses search the web, for example for whether something similar has been tried elsewhere. Only pages from vetted publishers are shown, apart from the research corpus and labelled (research, official, organisation, interest group, news). The search questions include the titles of the leverage points and solutions.',
    personAddTitle: 'Add a participant',
    personTitle: 'Title',
    personOrganisation: 'Organisation',
    personAdded: '{{who}} added to the list. The invitation goes out when you press Send invitations.',
    peopleInvitedCol: 'Invited',
    peopleNotInvited: 'not invited',
    peopleSignedIn: 'signed in {{date}}',
    peopleSelectAll: 'Select all',
    peopleResendOne: 'Send again',
    peopleSendOne: 'Send invitation',
    peopleSendInvites: 'Send invitations ({{count}})',
    peopleSendHint: 'The welcome mail to the ticked people. It can be sent again.',
    peopleInviteConfirm: 'Send the welcome mail to {{count}} participants?',
    peopleInvitedDone: 'Invitation sent to {{count}} participants.',
    peopleInviteFailed: 'Sent {{sent}}, failed {{failed}}: {{error}}',
    personAdd: 'Add participant',
    personAlreadyIn: '{{who}} was already on the list.',
    peopleFromFile: 'Add from file',
    peopleFileHint: 'Excel (.xlsx) or CSV. Header row: name, title, organisation, email — or a plain list of addresses.',
    peopleFileAdded: '{{rows}} rows in the file: {{added}} added, {{alreadyIn}} already in, {{skipped}} skipped (no address).',
    peopleSendInvitesTitle: 'Send the invitations?',
    peopleSendInvitesShort: 'Send',
    peopleInvitedOn: 'Invited {{date}}',
    peopleTitle: 'Participants of this Kuutio',
    peopleLead: 'This list is the Kuutio\'s participants: they see the Kuutio on their own page (/me) and receive the participant mails from the Viestit page.',
    peopleEmpty: 'No participants yet. Paste addresses below or import the Lyyti registrants.',
    peopleName: 'Name',
    peopleEmail: 'Email',
    peopleRole: 'Role',
    peopleLastLogin: 'Signed in',
    peopleNeverSignedIn: 'not yet',
    peopleRemove: 'Remove from the Kuutio',
    peopleRemoveConfirm: 'Remove {{who}} from this Kuutio\'s participants? The account stays.',
    peopleRemoved: '{{who}} removed from the Kuutio.',
    peopleImportLyyti: 'Import Lyyti registrants',
    peopleNoLyyti: 'This Kuutio has no Lyyti event.',
    peopleFromLyyti: '{{registrants}} registrants in Lyyti, {{added}} added.',
    role_participant: 'participant',
    role_facilitator: 'facilitator',
    role_admin: 'admin',
    role_owner: 'owner',
    importTitle: 'Participant list',
    importLead: 'Registrants (Lyyti) and their organisations are imported by script. They appear in the report\'s Participants section.',
    selectInstance: 'Choose an instance from the top bar.',
    loading: 'Loading…',
    loadFailed: 'Loading failed: {{error}}',
    saveFailed: 'Saving failed: {{error}}',
    basicPageTitle: 'Basics',
    basicPageLead: 'The cube\'s name, language and goal, its status, and clearing or deleting its data.',
    basicTitle: 'Basics',
    basicLead: 'The language drives both the interface and the text the AI produces.',
    kuutioNumber: 'Kuutio number',
    kuutioNumberHelp: 'Shown in the report header (KUUTIO N) and the report address (K<N>-…). The participant list and report images are looked up by it. Only admins and lead facilitators can change it.',
    kuutioNumberInvalid: 'The Kuutio number must be a positive whole number.',
    kuutioNumberTaken: 'Saved, but number {{number}} is already used by: {{titles}}.',
    statusTitle: 'Status',
    statusLead: 'The status normally moves with the stages (Move to running, Move to results). Set it by hand only for exceptions.',
    statusHelp: 'Draft and Archived hide the cube from participants.',
    dangerTitle: 'Danger zone',
    dangerLead: 'Clearing removes participant and AI output but keeps the preparation. Deleting removes the whole cube.',
    cleared: 'Cleared: {{count}} rows.',
    contentPageTitle: 'Content',
    contentPageLead: 'Everything known before the Kuutio: scenarios and variables, and the inputs of each module in the flow. Decisions made during the Kuutio happen in the Workshop.',
    scenariosLead: 'Name, description, icon and colour. Variables open from each scenario\'s own button; they become the impact map\'s starter nodes.',
    participantsPageTitle: 'Participants',
    participantsPageLead: 'How participants and devices get into the cube.',
    accessTitle: 'Access',
    accessLead: 'The workshop PIN is the code the room signs in with. It is shown in the run strip during the workshop.',
    openPinHelp: 'Test mode: any PIN opens this cube. Keep it off in a real workshop.',
    remoteEntryLabel: 'Workshop reachable from the personal page',
    remoteEntryHelp: 'Shows participants the Enter the workshop button on their page (/me). Off by default; switch on when the workshop starts.',
    datasetsPageTitle: 'Knowledge base',
    datasetsPageLead: 'Which research material this cube\'s AI can search.',
    datasetsLead: 'The base dataset is available to every cube unless excluded. Other datasets are attached to this cube separately.',
    prepareTitle: 'Preparing knowledge bases',
    prepareLead: "A Kuutio's research material is collected and reviewed under Aineistot → Knowledge bases, before the knowledge base is attached to this instance.",
    openPrepare: 'Open Knowledge bases',
  },
  datasetPanel: {
    title: 'RAG knowledge bases',
    base: 'base',
    excluded: 'excluded',
    includeBase: 'Include base',
    excludeBase: 'Exclude base',
    detach: 'Detach',
    attach: 'Attach',
    newIdPlaceholder: 'new-dataset-id',
    namePlaceholder: 'Name',
    createAttach: 'Create & attach',
  },
  exportPage: {
    title: 'Export',
    lead: 'Taking the workshop out of Kuutio: its whole content, research material and copies.',
    workshopTitle: 'Workshop as JSON',
    workshopLead: 'Scenarios, variables, paths, pivot points, lenses, solutions, transcripts, report and flow. Personal data is removed.',
    workshopButton: 'Download workshop (JSON)',
    ragTitle: 'Research material',
    ragLead: 'This cube\'s RAG material as JSON.',
    ragButton: 'Download material (JSON)',
    cloneTitle: 'Clone and export',
    cloneLead: 'A copy of this Kuutio under a new name: on this server, or as a clone package for another environment. Nothing existing is overwritten.',
    archiveTitle: 'Archiving',
    archiveLead: 'An archived cube is hidden from participants; the report and data are kept.',
    archiveButton: 'Set the status in Basics',
    downloading: 'Downloading…',
    failed: 'Download failed: {{error}}',
  },
  clone: {
    nameLabel: 'Name of the clone',
    namePlaceholder: 'New name, e.g. TEST: {{name}}',
    nameFree: 'The name is free.',
    nameTaken: 'Name already in use: {{name}}',
    nameInvalid: 'Give a name with letters or digits.',
    checking: 'Checking…',
    modeLabel: 'What is copied',
    modePrepared: 'Prepared content only',
    modePreparedHelp: 'Settings, scenarios and variables, lenses, the workshop flow, screens, organisations and linked datasets.',
    modeEverything: 'Everything',
    modeEverythingHelp: 'Also participant and AI output: paths, lens content, pivot points, solutions, transcripts and the report.',
    neverCopied: 'PINs, participants, votes, chats and logs are never copied. The clone gets its own PINs.',
    includeFiles: 'Include the original files (PDFs)',
    includeFilesHelp: 'Without the files the package is smaller, but sources cannot be converted again.',
    sameServerTitle: 'Clone on this server',
    cloneButton: 'Clone',
    cloning: 'Cloning…',
    cloned: 'Done: {{name}}',
    openClone: 'Open the clone',
    exportInstance: 'Export Kuutio',
    exportDataset: 'Export dataset',
    exportHelpInstance: 'Downloads a clone package (.zip). Import it in the other environment: Instance management → Import Kuutio.',
    exportHelpDataset: 'Downloads a clone package (.zip). Import it in the other environment: Aineistot → Knowledge bases → Import dataset.',
    exporting: 'Packing…',
    importInstance: 'Import Kuutio',
    importDataset: 'Import dataset',
    importLeadInstance: 'A clone package (.zip) from another environment or this server. The Kuutio gets a new name, and nothing existing is overwritten.',
    importLeadDataset: 'A dataset clone package (.zip). The dataset gets a new name, and nothing existing is overwritten.',
    datasetSectionTitle: 'Clone and export',
    fileLabel: 'Clone package',
    reading: 'Reading the package…',
    packageFrom: 'Environment {{env}}, exported {{date}}',
    packageRows: '{{count}} rows, {{tables}} tables',
    modeIs: 'Content: {{mode}}',
    schemaProblems: 'This server lacks parts of the schema the package needs. Update the server first:',
    missingDatasets: 'These datasets are not on this server, so they are not linked: {{ids}}',
    importButton: 'Import',
    importing: 'Importing…',
    failed: 'Failed: {{error}}',
    warningsTitle: 'References that could not be kept:',
    warningCleared: '{{table}}.{{column}}: {{rows}} references cleared ({{refTable}} is not here)',
    warningDropped: '{{table}}: {{rows}} rows left out ({{refTable}} is not here)',
    reindexStarted: 'The workshop\'s own RAG data is being rebuilt in the background.',
    reindexFailed: 'Rebuilding the workshop\'s RAG data did not start: {{error}}',
  },
  scriptHint: {
    skill: 'Done with a script. Instructions: skill {{skill}}.',
  },
  reportStatus: {
    title: 'Report material',
  },
  reportSettings: {
    title: 'Report settings',
    implementationPlans: 'Show "How do we start?" sections',
    implementationPlansHelp: 'Implementation plans are shown in the report\'s solutions section.',
    reportPin: 'Report PIN',
    reportPinHelp: 'Sections marked protected open with this code. Empty = no protected sections.',
    sectionAccess: 'Section visibility (public, protected, hidden) is chosen in the report\'s edit mode.',
    openEditor: 'Open the report',
  },
  reportStep: {
    intro: 'Creating the report, its images and summary are this section\'s actions; here the material, translations, settings and exclusions.',
    stage: 'Stage',
    inResults: 'In the Tulokset stage',
    notInResults: 'Not yet in Tulokset (action Move to results)',
    history: 'Report history',
    loadFailed: 'Could not load the report status: {{error}}',
    created: 'Created',
    notCreated: 'Not created yet',
    translations: 'Translations',
    noTranslations: 'No translations',
    openReport: 'Go to the report',
    moveToResults: 'Move to results',
  },
  pivotsV2Step: {
    intro: 'Pivot points are found, accepted and dismissed in the pivot point view.',
    loadFailed: 'Could not load pivot points: {{error}}',
    accepted: 'Accepted',
    proposed: 'Proposed',
    dismissed: 'Dismissed',
    noRun: 'No pivot points have been searched for yet.',
    open: 'Open pivot points',
  },
  adminRunStrip: {
    pinLabel: 'PIN',
    instancePin: 'Workshop PIN',
    pinTitle: 'Participant PIN codes',
    copy: 'Copy',
    copied: 'Copied',
    openParticipantView: 'Open the participant view with this PIN in a new window',
    noPins: 'No PIN codes. Add them on the Participants page under Preparation.',
    pinsFailed: 'Could not load PIN codes: {{error}}',
    locked: 'Locked',
    open: 'Open',
  },
  screenContent: {
    title: 'Screen content',
    description: 'What each screen shows in each phase. Planned in advance; during the workshop, devices are controlled from the Devices page.',
    loadFailed: 'Could not load the flow: {{error}}',
  },
  workshopLiveControls: {
    title: 'Live controls',
    statusLabel: 'Status',
    openPinLabel: 'Open PIN (test)',
    votingLabel: 'Solution voting',
    scoreLabel: 'Solution scores',
    screenControlLabel: 'Screen control',
    screenControlHint: 'When on, /screen controls wall displays',
    saving: 'Saving…',
    saved: 'Saved',
  },

  instanceSettings: {
    language: 'Workshop language',
    languageHelp: 'Controls the UI and AI response language (FI / EN / SV).',
  },

  artifact: {
    collection: 'Collection',
    collectionItems: 'Pages ({{n}})',
    eyebrow: 'Kuutio agents',
    loading: 'Loading…',
    notFound: 'Page not found',
    revoked: 'This page has been withdrawn',
    needsSignIn: 'This page needs a sign-in',
    failed: 'The page could not be loaded',
    summary: 'Summary in the chat',
    summaryLead: 'What the asker saw in the chat once the thread closed.',
    noSummary: 'No separate summary.',
    report: 'Report',
    reportLead: 'Written from the turns and their ordering, nothing else.',
    history: 'History: how the answer came about',
    historyLead: 'Every step in the order it happened. Each voice sees only its own base, its own sources and the others\' last turn.',
    sources: 'Sources',
    sourcesLead: 'One number is one source across the whole answer. Each was retrieved or read from the voice\'s own Kuutio, never recalled.',
    asker: 'Asker',
    assistant: 'Assistant',
    turnSources: 'Sources this turn cited ({{n}})',
    filterStep: 'The filter orders (proposes nothing, adds nothing)',
    synthesisStep: 'The synthesis gathers',
    reportStep: 'The report is written',
    reportStepNote: 'From the turns and the ordering; section 2 above.',
    summaryStep: 'summary to the chat',
    summaryStepNote: 'Section 1 above.',
    noOrdering: 'The ordering was not stored in this run.',
    agreed: 'Agreed',
    disputed: 'Disputed',
    open: 'Open',
    findings: 'Findings',
    tensions: 'Tensions',
    gaps: 'Gaps',
    implications: 'Implications',
    disclaimer: 'The voices are syntheses of past Kuutios, this workshop\'s own data or synthetic target-group agents from an opinion dataset, not participants or organisations. Costs are estimates from the price list.',
    published: 'Published {{date}}',
  },
  chatAgent: {
    introTitle: "Hi! I'm Kuutio's AI assistant.",
    introBody: 'I know the workshop material, its research background, and the participants’ ideas. Ask freely — I can help you make sense of context, compare perspectives, or summarize discussions.',
    introDisclaimer: 'Don’t take everything I say as absolute truth — I’m an AI and I have my limits.',
    tryTip: 'Try the Kuutio assistant',
    inputPlaceholder: 'Ask me anything...',
    askMeAnything: 'Ask me anything',
    errorPrefix: 'Error: {{message}}',
    connectionError: 'Connection error. Please try again.',
    modelTitle: 'AI model',
    clearTitle: 'Clear conversation',
    clearConfirm: 'Clear the conversation? This cannot be undone.',
    clear: 'Clear',
    assistantTitle: 'Kuutio Assistant',
    assistantSubtitle: 'Research-powered insights',
    assistantSubtitleHeadai: 'HeadAI skills & semantics',
    selectModelTitle: 'Select AI model',
    closeChat: 'Close chat',
    jumpToLatest: 'Jump to latest',
    agentsRowTitle: 'Kuutio agents:',
    agentIncluded: 'In the discussion — tap to leave out',
    agentExcluded: 'Left out — tap to include',
    agentsSelectLabel: 'Kuutio agents {{included}}/{{total}}',
    agentsSelectTitle: 'Choose which Kuutio agents are in the discussion',
    agentInfoTitle: 'About this agent',
    agentInfoSynthesis: 'expert voice of a past Kuutio',
    agentInfoTopic: 'Topic',
    agentInfoNoPerspective: 'No perspective written yet.',
    agentInfoDisclaimer: 'Speaks from its own Kuutio\'s standpoint: its output and knowledge base are its experience and evidence. Marked points are the workshop\'s results; the rest is the agent\'s reasoning from that angle. It is the workshop\'s voice, not a person or an organisation.',
    askAgentPrefix: 'Ask {{name}}:',
    askAgentButton: 'Ask {{name}}',
    agentsTogether: 'Kuutio agents together',
    agentThin: 'thin base',
    agentThick: 'thick base',
    agentSynthetic: 'synthetic',
    agentInfoOpinion: 'synthetic target-group agent from an opinion dataset',
    agentInfoDisclaimerOpinion: 'Represents the distribution of answers in an opinion dataset: the groups are statistical reconstructions from a self-selected online discussion, not people and not a representative sample. Every claim rests on the dataset\'s statements and their per-group figures; the rest is the agent\'s reasoning from the groups\' profiles. It is not a person and is never quoted as a participant.',
    agentDocumented: 'documented position',
    agentInfoParty: 'a party\'s documented position from public documents',
    agentInfoDisclaimerParty: 'Represents a party\'s documented position on one subject: its programmes, the government programme that binds it and its parliamentary group\'s dated plenary speeches, all public documents. It is a group, never a person; every claim rests on a marked document, the rest is the agent\'s inference. A reconstruction the party itself may read and dispute.',
    tools: {
      listAgents: 'Listing Kuutio agents...',
      askKuutio: 'A Kuutio agent is answering...',
      bounce: 'The agents are arguing the question out...',
      deepDive: 'The voices are analysing the question in depth...',
      headaiPlaybook: 'Loading HeadAI playbook...',
      headaiAnalysis: 'Fetching HeadAI analysis...',
      searchResearch: 'Searching research...',
      workshopData: 'Fetching workshop data...',
      solutions: 'Fetching solutions...',
      pivotPoints: 'Fetching pivot points...',
      lensAnalysis: 'Fetching lens results...',
      workshopNodes: 'Fetching impact map nodes...',
      impactPath: 'Fetching impact path...',
      organisations: 'Fetching organisations...',
      fallback: 'Fetching data...',
    },
    thread: {
      kind: {
        bounce: 'Bounce',
        deep_dive: 'Deep dive',
      },
      findings: 'findings {{n}}',
      tensions: 'tensions {{n}}',
      gaps: 'gaps {{n}}',
      turns: '{{n}} turns',
      rounds: '{{n}} rounds',
      running: '{{done}}/{{total}} spoken',
      writing: 'The agents are speaking; the filter and the report follow…',
      agreed: 'agreed {{n}}',
      disputed: 'disputed {{n}}',
      open: 'open {{n}}',
      converged: 'no real disagreement',
      share: 'Share',
      shareCopied: 'Link copied',
      shareTitle: 'Publish as a public page and copy the link',
      sharePublic: 'public link',
      shareSignIn: 'Sharing needs a sign-in (admin).',
      openReport: 'Open report',
      noReport: 'the report could not be written',
      reportTitle: 'The agents\' report',
      download: 'Download as Markdown',
      close: 'Close',
      reportDisclaimer: 'The agents are the voices of past Kuutios, not participants or organisations. Source markers [n] match the chat\'s source list.',
    },
    split: {
      all: 'all',
      note: 'Share of those who took a position. The numbers describe the participants of a self-selected online discussion, not a population; the groups are statistical reconstructions from the votes.',
    },
    usage: {
      calls: '{{n}} calls',
      tokens: '{{n}} tokens',
      cost: '≈ ${{usd}}',
      costUnknown: 'price unknown',
    },
    deepDiveButton: 'Run the deep dive',
    deepDivePrefix: 'Run a deep dive:',
    reactions: {
      accept: 'Accept',
      reject: 'Reject',
      fix: 'Fix',
      acceptHint: 'This holds',
      rejectHint: 'This does not hold or does not fit',
      fixHint: 'Right direction, something is off',
      why: 'Why?',
      reasons: {
        wrong_owner: 'wrong owner',
        impossible_mandate: 'impossible mandate',
        tried_already: 'tried already',
        politically_dead: 'politically dead',
        wrong_scale: 'right but wrong scale',
      },
    },
  },

  // Tilannekuva — live workshop dashboard (specs/workshop-dashboard.md)
  dashboard: {
    title: 'Dashboard',
    live: 'Live',
    stale: 'No data connection',
    noData: 'No data yet',
    notStarted: 'Workshop not running',
    paused: 'Paused',
    ragCounter: '{{total}} sources · {{retrieved}} retrieved',
    costToday: 'today {{count}} calls · ${{cost}}',
    chatToday: 'today {{count}}',
    estimatedEnd: 'Estimated end {{time}}',
    close: 'Close',
    noInstance: 'No workshop selected — open this view from a workshop link.',
    tiles: {
      flow: 'Workshop flow',
      progress: 'Step progress',
      rag: 'Knowledge base',
      cost: 'AI cost',
      feed: 'Activity',
      schedule: 'Schedule',
      chat: 'Chat',
    },
    units: {
      notes: 'notes',
      lensItems: 'items',
      solutions: 'solutions',
      pivotPoints: 'pivot points',
      kipupisteet: 'problems',
      ankkurit: 'obstacles to change',
      juurisyyt: 'root causes',
      v2Nodes: 'nodes',
      scenarios: 'scenarios',
      questions: 'questions',
      futuresChosen: 'futures chosen',
      fieldAnswers: 'fields answered',
      votes: 'votes',
      painPoints: 'pain points',
      drivers: 'approved drivers',
      wildCards: 'wild cards',
      pathSteps: 'path steps',
      futuresApproved: 'preferred futures approved',
      timelines: 'timelines simulated',
      leveragePoints: 'leverage points',
      pivotThemes: 'themes',
    },
    selected: 'selected',
    noPivot: 'No pivot point',
    openPivotSummary: "Open the pivot point's summary",
  },

  ragSourceMap: {
    title: 'Knowledge base',
    loading: 'Loading sources…',
    empty: 'No sources available for this instance.',
    error: 'Could not load the source map.',
    retrievals: 'retrievals',
    baseCluster: 'Base collection',
    notRetrieved: 'Not yet retrieved',
    counter: '{{total}} sources · {{retrieved}} retrieved',
    resetView: 'Reset view',
    voices: {
      valtio: 'State',
      eduskunta: 'Parliament & statutory evaluators',
      kansainvaliset: 'International organisations',
      tutkimus: 'Research & science panels',
      ajatushautomot: 'Think tanks',
      elinkeinoelama: 'Business & consultancies',
      kunnat: 'Municipalities, labour & civil society',
    },
    contexts: {
      node: 'Impact map',
      lens: 'Lens analysis',
      solution: 'Solutions',
      pivot: 'Pivot points',
      systemic: 'Systemic map',
      chat: 'Chat',
      ai: 'AI assistant',
      other: 'Other',
    },
  },

  ragSourceMapV2: {
    title: 'Knowledge base (v2)',
    loading: 'Loading sources…',
    empty: 'No sources available for this instance.',
    error: 'Could not load the source map.',
    perustietopohja: 'Base collection',
    axis: {
      teema: 'grouped by theme',
      aani: 'grouped by voice',
    },
    teema: {
      teknologia: 'Technology',
      ilmasto: 'Climate',
      demokratia: 'Democracy',
      yhteiskunta: 'Society',
      governance: 'Governance',
    },
    voices: {
      valtio: 'State',
      kansainvaliset: 'International',
      tiede_tutkimus: 'Science & research',
      ajatushautomot: 'Think tanks',
      muut: 'Other',
    },
    counter: '{{total}} sources · {{retrieved}} retrieved',
    counterTotal: '{{total}} sources',
    defaultViewToggle: 'Default view',
    lockScale: 'Lock scale',
    switcherLabel: 'Switch Kuutio',
    retrievals: 'retrievals',
    chunksLabel: 'chunks',
    legendSizeUsage: 'Size = retrievals',
    legendSizeDefault: 'Size = chunks',
    tarkistettavaNote: '{{count}} need review (not shown)',
    notRetrieved: 'Not yet retrieved',
  },

  ragSourceMapV4: {
    pickInstance: 'Select instance',
    modeLahteet: 'Sources',
    modeKaytto: 'Usage',
    breadthLabel: 'distinct queries',
    statsTitle: 'Usage by cluster',
    vizCluster: 'Cluster view',
    vizList: 'List view',
    vizBig: 'Big view',
    listOrder: {
      theme: 'Theme',
      size: 'Size',
      random: 'Random',
      cluster: 'Cluster',
    },
    filters: {
      teknologia: 'Technology',
      ilmasto: 'Climate',
      demokratia: 'Democracy',
      yhteiskunta: 'Society',
      governance: 'State',
    },
  },

  pivotPoints: {
    drawerTabLabel: 'Pivot points ({{count}})',
    loading: 'Loading pivot points...',
    retry: 'Try again',
    errors: {
      accessDenied: 'Your session has expired. Sign in again to see the pivot points.',
      clustersFailed: 'Loading the pivot points failed.',
      loadFailed: 'Loading the pivot point data failed.',
    },
    viewMode: {
      all: 'All',
      impactPaths: 'Impact paths',
      clusters: 'Clusters',
      pivotPoints: 'Pivot points',
      list: 'List',
      allTitle: 'Show all thoughts grouped by variable',
      clustersTitle: 'Show pivot points (semantic clusters) + individual thoughts',
      listTitle: 'Browsable list of pivot points',
    },
    method: {
      structural: 'Structural',
      structuralTitle: 'Structural analysis (MICMAC) — influence × dependence in the impact-path network. Always available.',
      semantic: 'Semantic',
      semanticTitle: 'Semantic pivot points (AI clusters)',
      semanticTitleEmpty: 'Semantic pivot points — generate with AI analysis first',
    },
    common: {
      back: 'Back',
      info: 'Details',
      zoomIn: 'Zoom in',
      zoomOut: 'Zoom out',
      zoomReset: 'Reset',
      previousPivot: 'Previous pivot point',
      nextPivot: 'Next pivot point',
      level: 'Level {{depth}}',
      membersCount: '{{count}} members',
      unknown: 'Unknown',
      pivotPoint: 'Pivot point',
      cluster: 'Cluster',
      human: 'Human',
      more: '+{{count}} more',
      participant: 'Participant: {{name}}',
      analysis: 'Analysis',
      typeLabel: 'Type:',
      votesLabel: 'Votes:',
    },
    memberModal: {
      title: 'Thought details',
    },
    list: {
      empty: 'No pivot points yet — use the Clusters view to find semantically related thoughts.',
      voteHint: 'Tap a pivot point to vote it as important',
      loginHint: 'Pivot points ({{count}}) — sign in to vote',
      count: '{{count}}',
      openDetails: 'Open pivot point details',
      vote: 'Vote',
      unvote: 'Remove vote',
    },
    branch: {
      tree: 'Tree',
      treeTitle: 'Switch to tree view',
      cardinal: 'Quadrant',
      cardinalTitle: 'Switch to quadrant view',
      snowflake: 'Snowflake',
      snowflakeTitle: 'Switch to snowflake view',
    },
    detail: {
      noDescription: 'No description',
      tabInfo: 'Info',
      tabPaths: 'Paths ({{count}})',
      tabMap: 'Map',
      description: 'Description',
      sourceAi: 'AI-detected',
      sourceFacilitator: 'Created by facilitator',
      confidence: '{{percent}}% confidence',
      crossScenario: 'Cross-scenario',
      linkedPaths: 'Linked paths ({{count}})',
      themes: 'Themes',
      noMembers: 'No paths',
      createdAt: 'Created: {{date}}',
      severity: 'Challenge severity',
      optimism: 'Solution optimism',
      leverageActor: 'Who can act',
      leverageEffect: 'Effect on the workshop goal',
      rootCause: 'Root cause — changing this would strongly move the goal',
    },
    drawer: {
      statsPivots: 'Pivot points',
      statsClustered: 'Clustered',
      statsCrossScenario: 'Cross-scenario',
      analysisSettings: 'Analysis settings',
      minParticipants: 'Min. participants',
      dataParticipants: 'Participants in data',
      humanNodes: 'Human nodes',
      minScenarios: 'Min. scenarios',
      maxResults: 'Max. results',
      minConfidence: 'Min. confidence',
      similarity: 'Similarity',
      sensitivityFallback: 'Moderate',
      belowThreshold: 'Data has only {{count}} participant(s), but the threshold is {{threshold}}. Analysis lowers the threshold automatically, but results may be thin.',
      aiModel: 'AI model',
      autoReports: 'Generate reports automatically',
      autoReportsHint: 'AI report for each pivot point (uses tokens)',
      preview: 'Preview',
      fullAnalysis: 'Full analysis',
      progressHint: '{{elapsed}} — this takes a few minutes',
      empty: 'No pivot points.',
      emptyStart: 'No pivot points yet.\nPivot points are run from workshop admin (/admin/workshop).',
      presets: {
        conservative: { name: 'Strict', description: 'Strong connections' },
        balanced: { name: 'Balanced', description: 'Default' },
        exploratory: { name: 'Broad', description: 'Weaker connections too' },
        discovery: { name: 'Discoveries', description: 'Pivot points, tensions, surprises' },
      },
      sensitivity: {
        strict: 'Strict (identical)',
        moderate: 'Moderate (same theme)',
        loose: 'Loose (related)',
      },
      aiModels: {
        sonnet: { description: 'Fast, good quality' },
        opus: { description: 'Best quality, slower' },
      },
    },
    structural: {
      nodes: 'Nodes',
      edges: 'Links',
      relays: 'Relays (leverage)',
      convergences: 'Convergence points',
      candidatesTitle: 'Structural pivot candidates',
      showStarters: 'Also show scenario variables',
      hideStarters: 'Hide scenario variables',
      empty: 'No connected nodes in impact paths.',
      influenceDep: 'inf {{influence}} · dep {{dependence}}',
      loadingPathways: 'Loading impact pathways…',
      loadFailed: 'Failed to load data: {{error}}',
      coverageMapTitle: 'Coverage map — influence by depth',
      networkFooter: 'The same structural analysis is available as a method toggle in the pivot view (Structural / Semantic). Next: betweenness centrality and an interactive network diagram.',
    },
    reachMap: {
      sizeLegend: '⬤ size = downstream coverage (influence, max {{max}})',
      hint: 'Lanes = depth in the causal chain (dependence). Leaf nodes (coverage 0) hidden.',
      nodeTitle: '{{label}}\ninfluence {{influence}} · dependence {{dependence}}',
    },
    quadrant: {
      driving: 'Driving',
      relay: 'Relays / critical',
      dependent: 'Dependent',
      autonomous: 'Autonomous',
    },
    spatial: {
      back: '← Back',
      noConnections: 'No connections',
      noAnalysis: 'No analysis available',
      linkedVariables: 'Linked variables ({{count}})',
      noScenarioInfo: 'No scenario information',
    },
  },

  // Participant entry screens
  tablet: {
    subtitle: 'Participant interface',
    selectType: 'Select type',
    typeIdea: 'Idea',
    typeConcern: 'Concern',
    typeQuestion: 'Question',
    typeScenario: 'Scenario',
    inputLabel: 'Write your idea, question or observation',
    placeholder: 'Write here...',
    clear: 'Clear',
    send: 'Send',
    ownInputs: 'Your inputs',
  },

  welcome: {
    subtitle: 'Welcome to the workshop',
  },

  scenarioSelector: {
    subtitle: 'Choose a future vision as the basis for building your impact pathway',
    noScenarios: 'No scenarios',
    noScenariosSubtitle: 'No scenarios have been defined for this instance',
    variableCount: 'Includes 6 variables as a ready starting point',
    initializing: 'Initializing...',
    startPathway: 'Start impact pathway',
  },

  startNode: {
    defaultTitle: 'Current state',
    defaultDescription: 'Start building your path to the future. Choose a starting point and create the first consequence.',
  },

  scenarioWelcome: {
    missingInstance: 'Instance missing from URL (?kid=...)',
  },

  // Solution Page
  solutionPage: {
    loading: 'Loading solution...',
    notFound: 'Solution not found',
    unknownError: 'Unknown error',
    backToReport: 'Back to report',
    printPdf: 'Print PDF',
    hybridBadge: 'Hybrid solution',
    scoreLabels: {
      impact: 'Impact',
      feasibility: 'Feasibility',
      innovation: 'Innovation',
      evidence: 'Evidence',
    },
    navTabs: {
      summary: 'Summary',
      description: 'Description',
      context: 'Context and resources',
      impact: 'Areas of impact',
      analysis: 'AI analysis',
      plan: 'Implementation',
    },
    workshopLabel: 'Kuutio — Solution proposal',
    workshopLabelWithTitle: 'Kuutio — {{title}}',
    humanWritten: 'written by participants',
    sourceSolutions: 'Combined original solutions',
    problemLabel: 'What problem does this solve?',
    resourcesLabel: 'Resources and actors',
    futureLabel: 'Why does this build a better future?',
    impactLabels: {
      mindsets: 'Mindsets',
      structures: 'Structures',
      behavior: 'Behavior',
    },
    showAiPlan: 'Show AI-generated implementation plan',
    hideAiPlan: 'Hide AI proposal',
    footer: 'Kuutio Workshop Platform — Solution proposal',
    createdDate: 'Created {{date}}',
  },

  // Solutions List Page
  solutionsList: {
    loading: 'Loading solutions...',
    instanceMissingError: 'Instance ID missing',
    addInstanceUrl: 'Add ?kid=... to URL',
    averageScore: '/ 5 average (old analyses)',
    evidenceLabel: 'evaluated with tags',
    total: 'total',
    groupSolutions: 'Group solutions',
    hybridSolutions: 'Hybrid solutions',
    hybridBadge: 'Hybrid',
    footer: 'Kuutio Workshop Platform — Solution proposals',
  },

  reportGen: {
    movedToWorkshop: 'Creating the report, its images, summary, translations, settings and exclusions are in the report section of Työpaja.',
    openWorkshop: 'Open Työpaja',
    title: 'Report',
    description: 'Create the workshop report, translate it and decide what the public report shows.',
    instanceFallback: 'Report',
    selectInstance: 'Select an instance from the top menu',
    creating: 'Creating...',
    create: 'Create report',
    openPublic: 'Open public report',
    history: 'History',
    empty: 'No reports yet',
    readers: {
      title: 'Report readers',
      lead: 'Invite people who did not take part in the workshop to read the report\'s protected sections and discuss it with Kuutio. Participants read the report already. A reader gets an invitation by email and signs in to the report with a one-time code.',
      emailPlaceholder: 'name@organisation.org',
      add: 'Add and invite',
      empty: 'No readers yet',
      invited: 'Invitation sent',
      inviteFailed: 'Reader added, but sending the invitation failed: {{error}}',
      alreadyParticipant: 'This person already has access to this Kuutio, so they read the report already.',
      invitedOn: 'Invited {{date}}',
      notInvited: 'Not invited',
      lastLogin: 'last signed in {{date}}',
      resend: 'Send the invitation again',
      remove: 'Remove reader',
    },
    type: {
      full: 'Full report',
      summary: 'Summary',
      pivotOnly: 'Pivot points',
      fallback: 'Report',
    },
    status: {
      lensSummariesRunning: 'AI summary created — compiling lens summaries...',
      plansRunning: 'Lens summaries compiled — generating implementation plans...',
      plansDone: 'AI summary created, implementation plans: {{generated}}/{{total}}',
      plansDoneExcluded: 'AI summary created, implementation plans: {{generated}}/{{total}}. {{excluded}} inputs left out of summaries',
      plansFailed: 'AI summary created, but implementation plans failed',
      createFailed: 'Report creation failed',
      deleteFailed: 'Deletion failed',
      translateRunning: 'Translating report ({{langs}})...',
      translateDone: 'Translation done: {{translated}} fields (skipped {{skipped}})',
      translateFailed: 'Translation failed',
      translatePartial: 'Translation finished with {{failed}} failed batches',
    },
    translate: {
      title: 'Translate report',
      button: 'Start translation',
      translating: 'Translating...',
      includeTranscripts: 'Include transcripts',
      pickLanguage: 'Select at least one target language',
      original: 'Original: {{lang}}',
      targets: 'Translate to:',
      existing: 'Stored translations',
      existingEmpty: 'No stored translations',
      existingCount: '{{count}} fields',
      remove: 'Remove',
      removing: 'Removing...',
      removeConfirm: 'Remove the {{lang}} translation for this report? This cannot be undone.',
      removeDone: '{{lang}} translation removed ({{deleted}} fields)',
      removeFailed: 'Failed to remove translation',
      panelTitle: 'Translations',
      addLanguage: 'Add languages',
      update: 'Update',
      updateTitle: 'Translate the changed texts again',
      latest: 'updated {{date}}',
      translateCount: 'Translate ({{count}})',
      uiNote: 'For languages other than Finnish, English and Swedish, the headings and buttons of the report are translated too, once per language.',
    },
    meta: {
      participants: '{{count}} participants',
      nodes: ' / {{count}} nodes',
    },
    exclusions: {
      title: 'Malformed inputs',
      hint: 'These inputs are left out of AI-compiled summaries. Participant cards still appear in report lists.',
      count: '{{count}} inputs left out of analyses',
      empty: 'No malformed inputs',
      loadFailed: 'Failed to load excluded inputs',
      includeAnyway: 'Include anyway',
      included: 'Included',
      forced: 'Excluded by facilitator',
      codes: {
        too_short: 'Too short',
        garbled: 'Garbled',
        test_data: 'Test data',
      },
      stages: {
        impact_path_node: 'Impact path',
        lens_item: 'Lens',
        solution: 'Solution',
      },
    },
  },

  // Print Report
  printReport: {
    chromeOnly: 'Chrome browser required',
    useChromeMessage: 'Please use Chrome browser for printing.',
    toolbarTitle: 'Kuutio #3 — Report (10 / 40 pages)',
    downloadPdf: 'Download PDF',
  },

  formCheck: {
    pendingLabel: 'Checking your input…',
    okLabel: 'Input looks good',
    warnLabel: 'This input has notes',
    dismiss: 'Dismiss',
    suggestedLabel: 'Did you mean this?',
    acceptSuggestion: 'Use suggestion',
    pill: {
      advisory: 'AI suggestion',
      blocking: 'Check the text',
    },
    tidyBtn: 'Tidy text',
    tidyFailed: 'Could not tidy the text. Please try again.',
    a11y: {
      pending: 'Input check in progress',
      ok: 'Input checked, no notes',
      warn: 'This input has notes',
    },
    issues: {
      too_short: 'Write a little more so the idea can be understood.',
      garbled: 'The text could not be read. Try writing it again.',
      test_data: 'This looks like test input. Write an idea related to the workshop.',
      off_topic: 'This does not seem related to the workshop theme.',
      wrong_stage: 'This idea would fit better in another workshop step.',
      multiple_ideas: 'This contains several ideas. Split them into separate entries.',
    },
  },

  instanceAccess: {
    pageTitle: 'Access',
    pageLead: 'Who may work on this Kuutio. Participants are managed on the Participants page.',
    sectionTitle: 'This Kuutio\u2019s staff',
    sectionLead: 'A manager changes settings, PINs and access; a facilitator runs the workshop and edits the report.',
    empty: 'No access granted. Administrators and the Kuutio\u2019s creator get in without a separate grant.',
    notAllowed: 'This Kuutio\u2019s access is changed by its manager or an administrator.',
    emailLabel: 'Email',
    emailPlaceholder: 'first.last@example.com',
    levelLabel: 'Level',
    add: 'Add',
    remove: 'Remove',
    actions: { manage: 'Manager', facilitate: 'Facilitator' },
  },
  usersScreen: {
    title: 'Users',
    lead: 'Every account, its role and its access. A new Sitra account arrives with no access — its role and first grants are given here.',
    tabUsers: 'Users',
    tabLinks: 'Links',
    filterPlaceholder: 'Filter by name or address…',
    roleLabel: 'Role',
    roles: { owner: 'Owner', admin: 'Administrator', usermanager: 'Lead facilitator', facilitator: 'Facilitator', participant: 'Participant' },
    lastLogin: 'Signed in {{date}}',
    neverLoggedIn: 'Never signed in',
    kuutio: 'Kuutio',
    dataset: 'Dataset',
    grantActions: { manage: 'manager', facilitate: 'facilitator', participate: 'participant', contribute: 'contributor', view: 'viewer' },
    dropGrant: 'Remove the grant',
    removeAccount: 'Remove the account',
    linksLead: 'Every link that lets someone in without signing in. Links are created in a dataset\u2019s settings.',
    noLinks: 'No links.',
    uses: '{{count}} uses',
    revoke: 'Revoke',
    revoked: 'revoked',
  },
  datasetWorkspace: {
    tabDocuments: 'Documents',
    tabDatasets: 'Knowledge bases',
    kind: 'Kind',
    kinds: {
      all: 'All',
      fact: 'Fact',
      policy: 'Policy',
      opinion: 'Opinion',
      testimony: 'Testimony',
      none: 'No kind',
    },
    searchLink: 'Web search',
    searchLinkHelp: 'The PDF gave no web address of its own, so the link is a web search for its name and publisher. Replace it with the real address once you find it.',
    profileFailed: 'The name, publisher and kind could not be read from the file: {{error}}. Fill them in by editing, or convert the source again.',
    pdfUpload: {
      title: 'Upload PDFs',
      lead: 'One or more PDFs, or ZIP files of PDFs. Each PDF becomes a source of its own and is added to the dataset\'s other sources. The name, publisher, link and kind (fact, policy, opinion) are read from inside the file, not from its file name.',
      drop: 'Drop PDF or ZIP files here, or choose them',
      picked: '{{count}} files chosen',
      upload: 'Upload ({{count}})',
      added: '{{count}} PDFs added; conversion starts.',
      skipped: 'Skipped, no PDFs: {{files}}',
    },
    manual: {
      title: 'Download by hand ({{count}})',
      lead: 'These links could not be downloaded automatically, most likely because the site blocks bots. Open the link, download the PDF in your own browser and upload it on its row. The name, publisher and link are kept.',
    },
    listTitle: 'Knowledge bases',
    listLead: "A Kuutio's research material is collected before the instance exists. Attach a finished knowledge base to an instance under Preparation → Knowledge base.",
    newDataset: 'New knowledge base',
    nameLabel: 'Name',
    namePlaceholder: 'e.g. Kuutio 9: Water security',
    topicLabel: 'Topic',
    topicPlaceholder: 'What the upcoming Kuutio is about',
    languageLabel: 'Language',
    create: 'Create knowledge base',
    open: 'Open',
    back: 'All knowledge bases',
    sourceCount: '{{count}} sources in the workspace',
    indexCount: '{{documents}} documents in search ({{chunks}} chunks)',
    cliDocs: {
      title: 'Indexed from the command line ({{count}})',
      lead: 'These documents were indexed with the command-line tool before the dataset workspace existed. They are in search ({{chunks}} chunks), but their files were never stored, so they can\'t be converted or re-indexed here.',
      show: 'Show documents',
      excluded: 'excluded from search',
    },
    attachedTo: 'Attached: {{instances}}',
    notAttached: 'Not attached to an instance yet',
    global: 'Base dataset',
    empty: 'No knowledge bases yet.',
    sourcesTitle: 'Sources',
    noSources: 'No sources yet. Upload PDFs above.',
    addSource: 'Add source',
    sourceName: 'Name',
    publisher: 'Publisher',
    link: 'Link',
    file: 'PDF (optional)',
    fileHelp: 'Without a PDF, the file is downloaded from the link. A web page is converted as is.',
    add: 'Add',
    status: 'Status',
    quality: 'Quality',
    chunks: 'Chunks',
    approve: 'Approve and index',
    reject: 'Reject',
    remove: 'Remove',
    save: 'Save',
    edit: 'Edit',
    cancel: 'Cancel',
    preview: 'Converted text',
    noPreview: 'No converted text yet.',
    pages: '{{pages}} pages, {{visual}} visual',
    confirmRemove: 'Remove this source?',
    fixTitle: 'Fix the source',
    uploadFile: 'Upload file',
    openPdf: 'Open PDF',
    replaceFile: 'Replace file',
    uploading: 'Uploading…',
    blockedHelp: "The site doesn't allow automatic downloads. Download the file from the link in your own browser and upload it here. The name, publisher and link are kept.",
    failedHelp: 'Fix the link by editing the source, or upload the file yourself.',
    awaitingHelp: 'This source has no file. Add a link by editing the source, or upload the file.',
    errorDetails: 'Error details',
    errors: {
      download_blocked: 'Error: automatic download blocked',
      download_failed: 'Error: download failed',
      generic: 'Error',
    },
    settings: {
      open: 'Settings',
      description: 'Description',
      global: 'Base dataset: every instance searches it',
      globalHelp: "A base dataset is visible in every Kuutio without attaching it, and several base datasets add up. A knowledge base can't be deleted: its chunks would silently drop out of search.",
      confirmGlobalOn: 'This becomes a base dataset, visible in all {{count}} instances right away. Continue?',
      confirmGlobalOff: "This stops being a base dataset: instances it isn't attached to no longer search it ({{count}} instances in total). Continue?",
      otherGlobals: 'Note: there are already base datasets: {{names}}. They are all searched together.',
    },
    access: {
      title: 'Access',
      lead: 'Who may use this dataset. A manager approves sources and changes settings, a contributor adds and fixes sources until they are approved, a viewer sees the sources and their status.',
      empty: 'No access granted. Administrators see every dataset without a separate grant.',
      emailLabel: 'Email',
      emailPlaceholder: 'first.last@example.com',
      levelLabel: 'Level',
      add: 'Add',
      remove: 'Remove',
      actions: { manage: 'Manager', contribute: 'Contributor', view: 'Viewer' },
      shareLabel: 'Send this link to the people you add. Those outside Sitra sign in with a code sent to their email:',
      copy: 'Copy',
      copied: 'Copied',
    },
    bulk: {
      select: 'Select',
      selectAll: 'Select all awaiting review ({{count}})',
      approve: 'Approve and index {{count}} (~${{cost}})',
      reject: 'Reject {{count}}',
      clear: 'Clear selection',
      confirmReject: 'Reject {{count}} sources?',
      approveDone: '{{count}} approved; indexing starts',
      rejectDone: '{{count}} rejected',
    },
    duplicates: {
      flag: 'duplicate?',
      detail: 'Same text as: {{titles}}',
    },
    text: {
      edit: 'Edit text',
      edited: 'edited',
    },
    vision: {
      run: 'Read images ({{pages}} pages, ~${{cost}})',
      confirm: 'The {{pages}} visual pages are read with image recognition, about ${{cost}}. The source is converted again and needs approval again. Continue?',
    },
    rerun: {
      redownload: 'Download again',
      reconvert: 'Convert again',
      reindex: 'Index again',
      confirmIndexed: 'The source is converted again and needs approval again. The current version stays searchable until the new one is indexed. Continue?',
      editIndexedHint: 'The change reaches search when the source is indexed again.',
    },
    results: {
      images: 'Images',
      imagesRead: 'read on {{described}}/{{pages}} pages',
      imagesNotRead: 'not read',
      conversion: 'Conversion',
      file: 'File',
      pages: 'Pages',
      matched: 'Matched',
      cost: 'Estimated cost',
      reviewReason: 'To check',
      rejectionReason: 'Rejection reason',
      index: 'Index',
      chunks: 'Chunks',
      words: 'Words',
      language: 'Language',
      topics: 'Topics',
      indexedAt: 'Indexed',
      quality: 'Quality',
      credibility: 'Credibility',
      researchBasis: 'Research basis',
      recency: 'Recency',
      documentType: 'Document type',
      publisherType: 'Publisher type',
      year: 'Publication year',
      bias: 'Perspective',
      none: 'No results yet.',
    },
    statuses: {
      draft: 'In import',
      unmatched_file: 'No source',
      awaiting_file: 'Awaiting file',
      pending_download: 'Downloading',
      pending_conversion: 'Converting',
      needs_review: 'Check',
      pending_review: 'Awaiting approval',
      approved: 'Approved',
      indexing: 'Indexing',
      indexed: 'Indexed',
      rejected: 'Rejected',
      failed: 'Failed',
    },
  },

  datasetImport: {
    alternative: 'Alternatively, a source list (Excel or CSV) and PDFs that are matched to its rows.',
    openForm: 'Import a source list',
    title: 'Import a source list and PDFs',
    lead: 'A source list (Excel or CSV: name, publisher, link) and a ZIP of PDFs. PDFs are matched to the list by file name, PDF metadata and their first pages. Conversion starts only when you confirm the matching.',
    sourcesLabel: 'Source list (.xlsx, .csv)',
    archiveLabel: 'PDF package (.zip)',
    filesLabel: 'Or individual PDFs',
    start: 'Import',
    uploading: 'Uploading…',
    extracting: 'Reading PDFs and matching them to sources…',
    failed: 'Import failed',
    summary: '{{matched}} matched, {{pdfs}} PDFs and {{rows}} rows without a pair',
    aiCalls: 'AI read {{count}} PDFs',
    matchedTitle: 'Matched',
    unmatchedPdfsTitle: 'PDFs without a source',
    unmatchedRowsTitle: 'Sources without a PDF',
    noUnmatchedPdfs: 'Every PDF is handled.',
    noUnmatchedRows: 'Every source has a PDF.',
    readAs: 'According to the PDF',
    unknownTitle: 'No title found',
    suggestions: 'Suggestions',
    selectPdf: 'Select PDF',
    selected: 'Selected',
    matchHere: 'Match the selected PDF here',
    unmatch: 'Unmatch',
    auto: 'automatic',
    manual: 'manual',
    own: 'own source',
    keepOwn: 'Own source',
    keep: 'Add as its own source',
    discard: 'Discard',
    excerpt: 'First page',
    rowNumber: 'row {{n}}',
    linkNote: 'Downloaded from the link after confirming',
    noLinkNote: 'No link: waits for a file',
    confirm: 'Confirm and start conversion',
    confirmBlocked: 'Handle every PDF first: match it, add it as its own source, or discard it.',
    cancel: 'Cancel import',
    confirmCancel: 'Cancel the import? Its rows and PDFs are removed.',
    methods: {
      filename_column: 'file name in the list',
      link_filename: "link's file name",
      title_in_text: 'title in the text',
      pdf_metadata: 'PDF metadata',
      first_page_title: 'title page',
      ai_title: 'title read by AI',
      filename: 'file name',
      text: 'words in the text',
    },
  },


  painPointVote: {
    title: 'What hurts the most?',
    intro: 'These pain-point groups were gathered from the pre-work. You have {{budget}} votes: give them to the ones the day should be spent on.',
    closedIntro: 'Voting has closed. This is the room’s result.',
    left: '{{left}} / {{budget}} votes left',
    closed: 'Voting is not open',
    noVotesLeft: 'Your votes are spent. Remove one to move it.',
    empty: 'No groups yet. The facilitator groups the pain points before the vote.',
    noInstance: 'No workshop selected.',
  },
  homework: {
    title: 'Homework',
    tabBefore: 'Before the Kuutio',
    tabAfter: 'After the Kuutio',
    prev: 'Previous',
    next: 'Next',
    empty: 'No answers yet.',
    answered: '{{count}} answered',
    mean: 'mean',
    agree: 'Agree', unsure: "Don't know", disagree: 'Disagree',
    holds: 'Holds', notHere: 'Not here',
    doneTitle: 'The room before the Kuutio',
    doneLead: 'This many did the pre-work. The day starts from what the room already knows.',
    invited: 'invited',
    didSelfAssessment: 'did the self-assessment',
    didKysely: 'answered the questionnaire',
    didPainPoints: 'named a pain point ({{count}} pain points)',
    selfAssessmentTitle: 'Self-assessment: how we rate ourselves',
    dividedTitle: 'Where we disagree',
    dividedLead: 'The most divided claims first. Start here.',
    claimsTitle: 'What holds true today',
    targetTitle: 'What the target must satisfy',
    expectTitle: 'Where we expect the workshop to end',
    painPointsTitle: 'Pain points',
    painPointsCount: '{{count}} pain points from {{people}} people',
    promoted: 'as a variable',
    ungrouped: '+ {{count}} ungrouped',
    peilausTitle: 'Mirrors: what the sources said, and whether it held',
    peilausLead: 'Participants took a stance on source claims from their own pain point.',
    mirrorsAnswered: '{{count}} mirrors',
    allMirrors: 'All mirrors',
    unknownSource: 'Unknown source',
    afterDoneTitle: 'The room after the Kuutio',
    didSelfAssessmentBefore: 'self-assessments before',
    didSelfAssessmentAfter: 'self-assessments after',
    noPost: 'No homework done yet.',
    changeTitle: 'Self-assessment before and after',
    beforeAfter: 'before {{before}} · after {{after}}',
    before: 'before',
    after: 'after',
  },
  clicker: {
    step: 'Step {{i}}/{{n}}',
    tila: 'State {{i}}/{{n}}',
    slide: 'Slide {{i}}/{{n}}',
    paused: 'Paused',
    notRunning: 'The workshop is not running.',
    prev: 'Previous',
    next: 'Next',
    start: 'Start the workshop',
    pause: 'Pause',
    resume: 'Resume',
    steps: 'Steps',
    stop: 'Stop',
    stopHint: 'Open the steps first; Stop ends the workshop.',
    phase: 'Step controls',
    phaseEmpty: 'This step has no controls.',
  },
  infoWall: {
    title: 'Kuutio · what is being built',
    week: 'week {{week}}',
    underWork: 'Under work',
    nextDemo: 'Next demo',
    noDemo: 'No demo agreed. Enter it on /admin/info.',
    shipped: 'Finished lately',
    theme: 'Theme',
    themeLight: 'light',
    themeDark: 'dark',
    doors: 'Doors',
    doorsHint: 'Scan to open on your phone',
    pulse: 'Pulse',
    pulseKuutiot: 'Kuutios held',
    pulseArticles: 'Articles in the knowledge base',
    pulseParticipants: 'Participants',
    pulseNext: 'Next Kuutio',
    pulseLenses: 'Obstacles and opportunities',
    pulseSolutions: 'Solutions',
    pulseUpcoming: 'Kuutios ahead',
    pulseNodes: 'Nodes on the maps',
    pulsePrework: 'Pre-work items done',
    vote: 'What interests you in Kuutio development?',
    voteHint: 'Tap one. The code takes you to answer on your phone.',
    voteAnswers: '{{n}} answers',
    voteQr: 'Questions and more on your phone',
    voteQrHint: 'One code: the ideas, three questions each, the stories and Kuutio\'s words.',
    kysyLead: 'Answer anonymously. Three questions per idea, when you want.',
    sanasto: 'Kuutio glossary',
    kysyTitle: 'Three questions',
    kysyThanks: 'Thanks! Your answers are stored, anonymously.',
    kysySend: 'Send',
    kysyMissing: 'Idea not found.',
    voteNoIdeas: 'No ideas to vote on. Label an issue with idea.',
    voteEmail: 'Your email',
    voteSend: 'Vote',
    voteCancel: 'Cancel',
    voteThanks: 'Thanks! Scan the code if you want to say more.',
    voteAgain: 'You had already voted for this.',
    voteRemoved: 'Vote taken back.',
    voteAsk: 'answer',
    voteHintOwn: 'Tap one. Answer the questions right here.',
    votes: '{{n}} votes',
    vaihe: { tuotannossa: 'In production', testissa: 'In testing', kokeilu: 'Experiment', tutkimus: 'Research' },
    stateOpen: 'coming',
    stateDone: 'done',
    empty: 'Nothing to show. Label an issue info.',
    lint: 'Stories without a wall sentence: {{list}}',
    noSource: 'No connection to GitHub.',
    upcoming: 'This week',
    upcomingNoDate: 'date open',
    calendarStale: 'calendar not updating',
    eventNew: '{{n}} new features',
    eventNotes: 'New',
    eventMinimise: 'Close',
    eventOngoing: 'Now',
    weeks: { next: 'Next week', later: 'Later', showNext: 'Show next week' },
    countdown: { kuutio: 'Kuutio {{n}}', days: '{{d}} d {{h}} h', hours: '{{h}} h' },
    weather: { clear: 'clear', partly: 'partly cloudy', cloudy: 'cloudy', fog: 'fog', drizzle: 'drizzle', rain: 'rain', showers: 'showers', snow: 'snow', thunder: 'thunder', feels: 'feels like {{t}}°', range: '{{lo}}…{{hi}}°' },
    lunch: { title: 'Lunch today', lead: 'Today\'s menus of the places nearby, read from their pages once a day.', loading: 'Reading the menus…', error: 'Could not read the menus: {{message}}', noMenu: 'No menu for today on the page.', noPlaces: 'No lunch places. Add them on /admin/info.' },
    tryOut: 'Try it',
    tryOutEmpty: 'Nothing to try yet. Add "Kokeile: /path" and "Kuva: /info/thumbs/N.jpg" lines to the card (or paste a screenshot into it).',
    tryOutBack: 'Back to the wall',
    tryOutOpen: 'Open in a new tab',
    tryOutPhone: 'Try it on your phone: scan the code and walk through.',
    event: { allDay: 'all day', organizer: 'Organiser', open: 'Open in Outlook' },
    story: {
      loading: 'Loading…',
      error: 'Could not load the card: {{message}}',
      body: 'Card',
      subIssues: 'Sub-tasks {{done}}/{{total}}',
      open: 'Open on GitHub #{{number}}',
    },
    demoEditor: {
      title: 'Demos',
      edit: 'Edit demos',
      loading: 'Loading…',
      signIn: 'Editing demos needs an admin sign-in.',
      date: 'Date',
      time: 'Time',
      place: 'Place',
      sessionTitle: 'Title (optional)',
      note: 'One line for the wall (optional)',
      slots: 'Who presents what',
      who: 'Who',
      what: 'What',
      noStory: '(no card)',
      slotNote: 'Description if needed',
      addSlot: 'Add a presentation',
      addPresenter: 'Add a presenter',
      up: 'Up',
      down: 'Down',
      noStories: 'No open cards under this name.',
      sessions: 'Demos',
      noSessions: 'No demos agreed.',
      addSession: 'Add a demo',
      remove: 'Remove',
      removeSession: 'Remove the demo',
      cancel: 'Close',
      save: 'Save',
      badDate: 'Date as YYYY-MM-DD.',
      error: 'Saving failed: {{message}}',
    },
    upcomingEmpty: 'No stories marked for this Kuutio yet.',
    noUpcoming: 'No known event. Enter it on /admin/info.',
    progress: '{{done}}/{{total}} done',
    admin: {
      title: 'Info wall',
      lead: 'What /info says about the next event and the next demo. Stories and ideas come from GitHub: labels info, idea and K9.',
      nextEvent: 'Next event',
      nextDemo: 'Next demo',
      eventTitle: 'Title (e.g. Kuutio 9)',
      date: 'Date',
      time: 'Time',
      place: 'Place',
      note: 'One sentence for the wall',
      eventHint: 'Left empty, the wall takes the next Kuutio from the instances. Stories whose milestone or label is this Kuutio show under it.',
      demoHint: 'Tick the stories in the demo\'s running order. The demo leaves the wall after its day; the card\'s assignee on GitHub is shown as its demoer.',
      demoItems: 'Stories shown in the demo',
      demoItemsNone: 'No open info stories.',
      lunch: 'Lunch places',
      lunchHint: 'The wall\'s lunch button reads these pages once a day and picks out today\'s menu. Name and the page with the menu.',
      lunchName: 'Restaurant',
      lunchAdd: 'Add a place',
      save: 'Save',
      saved: 'Saved',
      open: 'Open /info',
      loading: 'Loading…',
      error: 'Saving failed: {{message}}',
    },
  },

  me: {
    datasets: { title: 'Datasets', sources: '{{count}} sources' },
    heading: 'Your Kuutio',
    signInPrompt: 'Sign in with your email to see your Kuutio.',
    signIn: 'Sign in',
    enterWorkshop: 'Enter the workshop',
    openFacilitator: 'Open the facilitator view',
    enterLater: 'The workshop opens from this page when it starts.',
    enterOnSite: 'This workshop is joined on site. If remote entry is opened, you can reach the map from here.',
    signInInvited: 'Codes are sent only to invited participants. Use the address you registered with.',
    selfAssessment: {
      label: 'Self-assessment',
      postLabel: 'Self-assessment after the workshop',
      hint: 'Seven statements, two minutes.',
      postHint: 'The same statements as before the workshop, plus one.',
      intro: 'Rate your own foresight and innovation capability. Answer as you think now. The answers are for you and for evaluating the impact of Kuutio; nobody can read them under your name.',
      postIntro: 'The same statements as before the workshop. Answer as you think now, not as you answered then.',
      progress: '{{n}} / {{of}}',
      scale: { 1: 'strongly disagree', 2: 'somewhat disagree', 3: 'neither agree nor disagree', 4: 'somewhat agree', 5: 'strongly agree' },
      unknown: "Don't know",
      submit: 'Submit answers',
      finalNote: 'Answers are saved as final and cannot be changed afterwards.',
      done: 'Self-assessment done',
      doneNote: 'Thank you. Your answers are saved.',
      postDoneNote: 'Thank you. Your answers are part of evaluating the impact of Kuutio.',
      items: {
        1: 'I recognise my own and others\' assumptions about the future.',
        2: 'I use foresight to support decision-making in my organisation.',
        3: 'I can advance futures thinking in my own organisation.',
        4: 'I look at things systemically, recognising multi-dimensional cause-and-effect relationships.',
        5: 'I can use methods that support innovativeness and creativity in my work.',
        6: 'I can identify solutions to future challenges.',
        7: 'I am motivated to use foresight and innovation methods and tools in my work.',
        8: 'Working in Kuutio strengthened my foresight and innovation capability.',
      },
      info: {
        title: 'How your answers are stored',
        close: 'Close',
        stored: 'Your answers are stored in the Kuutio database with your email address, like your other pre-work. Only the Kuutio development team handles them.',
        paired: 'The answers show whether something changed through Kuutio. "No change" is a result too.',
        whoSees: 'The facilitator and Sitra see only how the room answered each statement in total. Nobody sees an individual person\'s answers.',
        purpose: 'Sitra uses the data to evaluate the impact of Kuutio: whether participants\' foresight and innovation capability grew through Kuutio.',
      },
    },
    kysely: {
      label: 'Questionnaire',
      hint: 'Twelve items, three minutes. The same for everyone.',
      intro: 'Answer from where you stand now. Answers are shown to the room only as totals, and nobody sees whose they are. This is your starting point; it is revisited after the workshop.',
      part: 'Part {{n}} / {{of}}',
      claimsQ: 'Which of these hold true today?',
      claimsHint: 'Tick every one you agree with.',
      stanceQ: 'Take a position on each statement.',
      socketsQ: 'Which of these must the target future meet?',
      socketsHint: 'Tick the ones you consider necessary.',
      expectQ: 'Where do you expect the workshop to end up?',
      expectHint: 'One choice. Revisited when the workshop is over.',
      expectFoot: 'Expectation before the workshop, compared with the outcome afterwards.',
      agree: 'Agree',
      unsure: 'Unsure',
      disagree: 'Disagree',
      submit: 'Send answers',
      finalNote: 'Sending is final. The same questions are asked again after the workshop.',
      done: 'Questionnaire answered',
      resultsIntro: 'How the room has answered so far. Your own answers are marked.',
      answered: '{{count}} answered',
      notReady: 'The questionnaire for this Kuutio has not been prepared yet. We will let you know when it is ready.',
    },
    online: 'Online. The link to the workshop opens on this page.',
    placeLater: 'The venue will be announced closer to the date.',
    kuutioRoom: 'The Kuutio room at Sitra',
    dateLater: 'The date will be announced closer to the time.',
    door: {
      emailLabel: 'Email address',
      emailPlaceholder: 'first.last@example.com',
      sendCode: 'Send code',
      codeSent: 'A code was sent to {{email}}. It is valid for 10 minutes.',
      codeLabel: 'Six-digit code',
      verify: 'Sign in',
      resend: 'Send a new code',
      resendIn: 'New code in {{seconds}} s',
      resent: 'A new code was sent.',
      changeEmail: 'Change address',
      signingIn: 'Signing in…',
      joining: 'You are joining the Kuutio "{{title}}".',
      joiningUnknown: 'A join link to a Kuutio.',
      noJoinCode: 'This Kuutio has no join code set. Ask the organiser for a new link.',
      howJoin: 'Sign in with your email. The code in the link adds you to this Kuutio.',
    },
    none: 'No Kuutio has been opened for you yet. If you expected to see one, contact the organiser.',
    loading: 'Loading…',
    failed: 'Could not load your details.',
    chooseWorkshop: 'You have more than one Kuutio. Choose which to view.',
    staffView: 'You see every Kuutio because you are an admin. A participant sees only their own.',
    back: 'Back',
    when: 'When',
    where: 'Where',
    directions: 'Show on map',
    contact: 'Questions?',
    goodToKnow: 'Good to know',
    tabPractical: 'Practical details',
    tabPreWork: 'Pre-assignments',
    tabHomework: 'Homework',
    homeworkIntro: 'The workshop is over. The homework is short, and it builds the picture of what changed.',
    homeworkTodo: 'Homework to do',
    preWorkClosed: 'The pre-work is locked: the workshop is over. Your answers stay as they were before the workshop.',
    preWorkClosedShort: 'Locked',
    noPreWork: 'No pre-assignments have been published yet. We will let you know when they are ready.',
    phone: 'Phone',
    assistant: 'Messages also to',
    preWorkIntro: 'Please complete the pre-work before the workshop. The day then starts from what you already know, and we get the most out of it.',
    preWorkBack: 'All pre-work',
    preWorkBackToList: 'Back to the pre-work list',
    preWorkDone: 'Done',
    countdown: { days: '{{days}} d {{hours}} h to Kuutio', hours: '{{hours}} h {{minutes}} min to Kuutio' },
    reading: {
      title: 'Pre-reading: the standard scenarios',
      body: 'Kuutio looks at its topic through four standard scenarios: democracy, population, technology and climate. Skim the scenario reports before the workshop so the futures are familiar when the work starts.',
      url: 'https://www.sitra.fi/kuutio',
      linkText: 'sitra.fi/kuutio',
    },
    preWorkLocked: 'Opens once Kipupiste is done',
    preWorkLockedAfter: 'Opens once “{{title}}” is done',
    chatham: {
      title: 'Chatham House rule',
      body: 'What is said in Kuutio may be used, but the speaker and their organisation are not named.',
    },
    peilaus: {
      intro: 'The knowledge base is mirrored against what you kept on the map: at most three places where a source says something different or adds a fact. You say whether it holds where you work. Disagreement is valuable.',
      pick: 'Choose a pain point',
      label: 'Mirror',
      start: 'Mirror',
      loading: 'Reading the knowledge base… this takes a moment.',
      you: 'You',
      source: 'Knowledge base',
      kindPain: 'Pain point',
      holds: 'Holds',
      not_here: 'Not where I work',
      unsure: 'Not sure',
      notePlaceholder: 'Why? (optional)',
      done: 'Thank you. Your stances are on the map as notes with their sources. We continue from here in the workshop.',
      empty: 'The knowledge base says nothing direct about this pain point. That too is information, and we return to it in the workshop.',
    },
    phase: { upcoming: 'Upcoming', ongoing: 'Ongoing', past: 'Past', online: 'Online' },
    preWorkTodo: 'Pre-assignments waiting',
    devices: { title: 'Devices', body: 'The work happens on Kuutio\'s own devices; you need not bring yours.' },
    comfort: { title: 'Wool socks and an easy attitude', body: 'Dress comfortably. Wool socks are welcome, and so is an easy attitude: Kuutio is a workshop, not a seminar.' },
    about: {
      title: 'Kuutio in brief',
      lines: [
        'Kuutio is Sitra\'s method for looking at a topic through possible futures and their cause-and-effect chains, and finding leverage points and solutions together. AI supports the work.',
        'Kuutio is an experiment. Dive in and trust the process.',
        'Only expert-checked material goes into the AI platform, and no personal data is entered into it.',
        'Sharing on social media is welcome: @Sitra on LinkedIn, @sitrafund on Instagram.',
      ],
    },
    transit: { subway: 'Metro', train: 'Train', tram: 'Trams', bus: 'Buses', more: '+{{count}} more' },
    preWorkUntitled: 'Pre-work',
    kipupiste: {
      scenario: 'Which scenario does this belong to?',
      aiSuggested: 'AI suggestion',
      defaultQuestion: 'What is the most burning problem around the day\'s topic?',
      intro: 'In your own words. I will then ask a few questions and offer what I heard as suggestions you can keep or drop. Only what you keep reaches the workshop map.',
      placeholder: 'For example: taxation does not reach platform economy income…',
      start: 'Start',
      answer: 'Answer',
      stop: 'That is enough for now',
      thinking: 'One moment…',
      answerPlaceholder: 'Answer in your own words',
      heard: 'What I heard',
      cause: 'Cause',
      blocker: 'Obstacle',
      keep: 'Keep',
      drop: 'Drop',
      kept: 'On the map',
      dropped: 'Dropped',
      done: 'Thank you. {{count}} things stayed on the map around your pain point. We continue from here in the workshop.',
      addAnother: 'Add another pain point ({{count}}/{{max}})',
      addFirst: 'Write a pain point',
      label: 'Pain point',
      causeOf: 'Behind: {{cause}}',
      mergedInto: 'Your pain point is now the variable “{{title}}” in the scenario {{scenario}}.',
      mergedWith: 'Together with {{count}} others.',
      cancelNew: 'Cancel',
      yours: 'Your pain points ({{count}}/{{max}})',
      inProgress: 'In progress',
      keptCount: '{{count}} on the map',
      limit: 'At most {{max}} pain points. Save the rest for the workshop.',
    },
    assistantIntro: 'You are the assistant for {{name}}. This is what the organiser has on file about them. It can be changed on their registration page.',
    theirDetails: 'Registration details',
    editTheirs: 'Change details',
    attendingCount: '{{count}} people registered',
    yourDetails: 'Your registration',
    detailsIntro: 'This is what the organiser has on file. You can change it on your own registration page.',
    editInLyyti: 'Change my details',
    organisation: 'Organisation',
    title: 'Title',
    allergies: 'Food allergies',
    diet: 'Dietary requirement',
    notGiven: 'Not given',
    noRegistration: 'We could not find your registration for this Kuutio. You are still welcome — contact the organiser if this looks wrong.',
    noEventInfo: 'Practical details will be confirmed closer to the date.',
    detailsUnavailable: 'Practical details cannot be fetched right now. Please try again shortly.',
  },

  futureSelection: {
    loading: 'Loading scenarios...',
    empty: 'No scenarios.',
    scenarioFallback: 'Scenario',
    futuresCount: '{{count}} future images',
    activeFuture: 'Active future image',
    customFuture: 'Own future image',
    freeBadge: 'Open',
    saving: 'Saving...',
    saved: 'Saved',
    noFutures: 'No future images',
    modalTitle: 'Change the future image?',
    modalExisting: 'Scenario {{scenario}} already has {{count}} participant-created nodes under the current future image {{previous}}.',
    modalRecommendation: 'Recommended: keep the participants\' content and swap only the future image + variables. Upcoming AI analysis will use the new future image {{next}}.',
    switching: 'Switching…',
    keepContent: 'Keep content, change future image',
    wipeAndRestart: 'Clear the impact path and start over',
    swapFailed: 'Changing the future image failed: {{error}}',
    wipeFailed: 'Clearing failed: {{error}}',
  },

  topicFocus: {
    selectedLabel: 'Selected perspective',
    noneSelected: 'None selected – all perspectives',
    noFilter: 'No focus',
    noFilterDescription: 'All perspectives are equally weighted',
    saving: 'Saving...',
    saved: 'Saved',
    focusLabel: 'Focus',
    original: 'Original: {{title}}',
  },

  pivotPointContent: {
    loading: 'Loading pivot points...',
    saveFailed: 'Saving the pivot point failed: {{error}}',
    deleteTitle: 'Delete pivot point',
    deleteMessage: 'Delete "{{title}}"? This cannot be undone.',
    clearTitle: 'Remove all selected pivot points?',
    clearMessage: 'This leaves the workshop with no selected pivot points — the Lenses, Solutions and report views will show nothing until you select new ones. Continue?',
    clearConfirm: 'Clear selections',
  },

  preworkCuration: {
    loading: 'Loading pre-work…',
    title: 'Pre-work: from pain points to variables',
    intro: 'The AI proposes the groups, titles and scenario. You decide what becomes a variable. Participants\' own words never change.',
    clustering: 'Grouping… this takes about a minute',
    recluster: 'Regroup',
    cluster: 'Group',
    countPainPoints: 'pain points',
    countParticipants: 'participants',
    countGroups: 'groups',
    countPromoted: 'variables on the map',
    unembedded: '{{count}} without a vector (grouping will embed them)',
    noPainPoints: 'Nobody has written a pain point yet.',
    noClusters: 'No groups yet. Press Group.',
    closeVote: 'Close voting',
    openVote: 'Open voting for the room',
    voteOpenInfo: 'Voting open: devices at /kipupisteet, {{budget}} votes per device. Votes move a group up the order.',
    voteClosedInfo: 'Once the groups are ready, the room can vote on them from their devices before you promote the variables.',
    scenarioFallback: 'Scenario',
    noScenario: 'No scenario',
    groupHeading: '{{title}} · {{count}} variables',
    unclusteredHeading: 'Ungrouped · {{count}}',
    clusterCard: {
      rankTitle: 'Order within the scenario: room votes, participants, disagreement, obstacles',
      namePlaceholder: 'Variable name',
      scenarioPlaceholder: 'Scenario…',
      votes: 'votes',
      participants: 'participants',
      painPoints: 'pain points',
      disagreement: 'disagreement',
      obstacles: 'obstacles',
      overCap: 'the scenario already has many variables',
      promoted: 'Variable on the map: {{title}}',
      undo: 'Undo',
      promote: 'Make a variable',
      pickScenarioFirst: 'choose a scenario first',
      dissolve: 'Ungroup',
    },
    painPoint: {
      move: 'Move…',
      ownGroup: 'Into its own group',
      untitled: '(untitled)',
      done: 'Pain point done',
      inProgress: 'Pain point in progress',
      causes: '{{count}} causes',
      blockers: '{{count}} obstacles',
      noVector: 'no vector',
      stance: {
        holds: 'applies',
        not_here: 'not for us',
        unsure: 'unsure',
      },
    },
    scenarioProposal: {
      fromAction: 'The proposal comes from the section\'s action Propose scenarios from the groups; it shows here for editing.',
      proposing: 'Proposing… about a minute',
      propose: 'Propose scenarios from the groups',
      form: 'Form scenarios from the groups',
      place: 'Place groups in existing scenarios',
      hintExisting: 'Scenarios already exist; the groups are placed in them, or the AI proposes new ones where nothing fits.',
      hintNone: 'The instance has no scenarios. The AI proposes them from the pain point groups and root causes; you edit and confirm.',
      heading: 'Proposal: {{count}} scenarios',
      editHint: 'Edit the titles and descriptions. Groups can be moved between scenarios after creation.',
      namePlaceholder: 'Scenario name',
      existingTag: 'existing',
      descriptionPlaceholder: 'Description',
      unplaced: 'Not placed: {{titles}}. They stay as groups, and you choose their scenario yourself.',
      create: 'Create and place',
      reject: 'Discard',
    },
  },

  solutionComponents: solutionComponentsEn,
  lensComponents: lensComponentsEn,
  moduleIntro: moduleIntroEn,
  languagePicker: {
    common: 'Common:',
    search: 'Search for a language by name or code (e.g. Estonian, eesti, et)',
    noMatch: 'No matches',
    remove: 'Remove {{lang}} from the choice',
  },
  report: reportStringsEn,
  foresight: { ...foresightEn, ...foresightScenarioEn, ...foresightPathEn, ...foresightWildcardEn },
};